import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Zap,
  Users,
  XCircle,
  ChevronLeft,
  ChevronRight,
  Star,
  User,
  Edit2,
  Filter,
  Copy,
  Clock,
  UserPlus,
  UserMinus,
  HeartPulse,
  RotateCcw,
  Trash2,
  Check,
  X as XIcon,
  CalendarClock,
  MapPin,
  List,
  LayoutGrid,
} from 'lucide-react';
import {
  format,
  addDays,
  startOfWeek,
  endOfWeek,
  addWeeks,
  subWeeks,
  startOfMonth,
  endOfMonth,
  isSameDay,
  parseISO,
} from 'date-fns';
import { es } from 'date-fns/locale';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { supabase } from '../services/supabase';
import { listaEnCastellano } from '../lib/leerVarias';
import { mensajeDelGuard } from '../lib/erroresDelGuard';
import { ROLES_ASIGNABLES } from '../lib/entrenadoresAsignables';
import { OpcionesDeEntrenador } from '../components/OpcionesDeEntrenador';
import { leerFichaMedica, leerFlagsFichaMedica } from '../services/fichaMedica';
import {
  videoBookingToEvent,
  isVideoAnalysisEvent,
  getVideoBookingId,
  type VideoAnalysisBookingRow,
} from '../lib/videoAnalysisAgenda';
import { useAgendaHours } from '../lib/useAgendaHours';
import AgendaHoursControl from '../components/AgendaHoursControl';
import Modal from '../components/ui/Modal';
import Confirm from '../components/ui/Confirm';
import ConflictWarningModal, { type ScheduleConflict } from '../components/ui/ConflictWarningModal';
import SedeFilter from '../components/SedeFilter';
import TimeGrid, { type TimeGridEvent } from '../components/TimeGrid';
import { loadActiveSedes, type Sede } from '../services/sedes';
import type { Database } from '../types/database.types';
import type { SessionStatus, GroupLevel } from '../types/database';

type SessionRow = Database['public']['Tables']['sessions']['Row'];
type EnrollmentRow = Database['public']['Tables']['enrollments']['Row'];

// Mismo Pick reutilizado dentro y fuera de Session — perfil mínimo del monitor.
type MonitorBrief = { id: string; full_name: string; role?: string | null };

// Forma cruda devuelta por el select:
//   sessions.select('*, groups(...profiles!monitor_id(...)), special_classes(...profiles!monitor_id(...)), profiles!monitor_id(...)')
// PostgREST devuelve embeds como objeto (many-to-one); supabase-js sin
// `<Database>` no infiere la dirección. La forma se compone aquí.
type SessionRowWithRelations = SessionRow & {
  groups: {
    name: string;
    level: GroupLevel;
    class_type: string;
    sedes: { name: string } | null;
    profiles: MonitorBrief | null;
  } | null;
  special_classes: {
    name: string;
    sedes: { name: string } | null;
    profiles: MonitorBrief | null;
  } | null;
  profiles: MonitorBrief | null;
  // Sede propia de la sesión (override sessions.sede_id, mig 163). Embed por la
  // FK sessions_sede_id_fkey; NULL = la sesión hereda la sede del contenedor.
  session_sede: { name: string } | null;
};

// Embed que pide `openDetail` sobre enrollments: (full_name, phone).
//
// mig 282: `medical_info` YA NO se hidrata aquí. Abrir el detalle de una sesión
// leía la ficha de todos sus inscritos —y con la 280, de cualquier sesión del
// club— sin que quedara rastro. Ahora la lista solo sabe QUIÉN tiene ficha
// (`tiene_ficha`), por un flag que sale del mismo gate que el contenido, y el
// contenido se pide por la RPC auditada `get_medical_info` al abrirla.
type EnrollmentWithProfile = EnrollmentRow & {
  profiles: { full_name: string; phone: string | null; tiene_ficha: boolean } | null;
};

async function hydrateFlagsFicha(rows: EnrollmentWithProfile[]): Promise<void> {
  if (rows.length === 0) return;
  const userIds = Array.from(new Set(rows.map((r) => r.user_id)));
  const { conFicha } = await leerFlagsFichaMedica(userIds);
  for (const row of rows) {
    if (row.profiles) {
      row.profiles.tiene_ficha = conFicha.has(row.user_id);
    }
  }
}

// Resultados de las RPCs SECURITY DEFINER del flujo de inscripción.
// Las funciones devuelven `Returns: Json` en el generador — definimos aquí
// el shape real del proyecto (convenio EnrollResult / staff_*).
type StaffAddResult = { ok: true } | { ok: false; error: string };
type StaffRemoveResult =
  | { ok: true; next_promoted_user_id?: string; next_promoted_push_token?: string }
  | { ok: false; error: string };
type UpdateCapacityResult = { ok: true; promoted?: number } | { ok: false; error: string };

interface Session {
  id: string;
  group_id: string | null;
  date: string;
  time_start: string;
  time_end: string;
  max_students: number;
  current_count: number;
  status: SessionStatus | string;
  is_special_class?: boolean;
  special_class_id?: string | null;
  monitor_id?: string | null;
  allow_subscription?: boolean;
  // Override de sede de la sesión (mig 163): id + nombre. NULL = hereda del
  // contenedor. `sessionSede()` resuelve la sede EFECTIVA con este override.
  sedeOverrideId?: string | null;
  sedeOverride?: string | null;
  group?: {
    name: string;
    level: GroupLevel | string;
    class_type?: string;
    sede?: string | null;
    monitor?: MonitorBrief | null;
  } | null;
  specialClass?: { name: string; sede?: string | null; monitor?: MonitorBrief | null } | null;
  monitor?: MonitorBrief | null;
}

interface Group {
  id: string;
  name: string;
  class_type: string;
  time_start: string;
  time_end: string;
  days_of_week?: number[];
  monitor?: MonitorBrief | null;
}

// Propuesta de horario del alumno para su tecnificación individual (motor común,
// mig 152): user_subscriptions class_type='individual' status='purchased' con
// proposed_at. El admin la gestiona desde la agenda: crea la sesión individual
// (generate_sessions, aforo 1) + activa la sub → J4 inscribe las 4 sesiones.
interface PendingProposal {
  id: string; // user_subscriptions.id
  user_id: string;
  student_name: string;
  proposed_day_of_week: number;
  proposed_time_start: string;
  proposed_at: string;
}

interface SpecialClassTemplate {
  id: string;
  name: string;
  time_start: string;
  time_end: string;
  max_students: number;
  level: string;
}

const LEVEL_COLORS: Record<string, string> = {
  basico: 'border-silver/50',
  medio: 'border-gold-primary/50',
  avanzado: 'border-warning/50',
  tecnificacion: 'border-success/50',
};

// C-2 — Agenda tipo Outlook: paleta hex para los bloques de la rejilla
// (LEVEL_COLORS son clases de borde, no sirven aquí). El rango horario es
// preferencia por usuario (C-4, useAgendaHours), ya no una constante.
const AGENDA_LEVEL_HEX: Record<string, string> = {
  basico: '#4CAF50',
  medio: '#2196F3',
  avanzado: '#FF9800',
  tecnificacion: '#FFF200',
};

// Convención DOW PostgreSQL: 0=domingo … 6=sábado.
const DAY_LABELS = ['Domingo', 'Lunes', 'Martes', 'Miércoles', 'Jueves', 'Viernes', 'Sábado'];

// Errores de activate_subscription al confirmar una propuesta individual.
const PROPOSAL_ERRORS: Record<string, string> = {
  no_matching_group:
    'No hay un grupo individual activo para ese día/hora. Créalo en Grupos y vuelve a intentarlo.',
  membership_required: 'El alumno no tiene la matrícula vigente. No se puede activar.',
  already_activated: 'Esta suscripción ya estaba activada.',
  not_activatable: 'Esta suscripción no está en estado activable.',
  month_in_past: 'El mes elegido ya pasó. Ajusta la fecha de inicio.',
  subscription_not_found: 'No se encontró la suscripción.',
  not_authorized: 'No autorizado.',
};

// Códigos de error de admin_move_session (mig 164) → mensaje para el admin.
// `slot_taken` es el BLOQUEANTE clave: la colisión del UNIQUE (mig 161).
const MOVE_ERRORS: Record<string, string> = {
  slot_taken:
    'Ya existe una sesión de este grupo en esa franja con ese entrenador. Cambia la fecha, la hora o el entrenador.',
  not_scheduled: 'Solo se pueden mover sesiones programadas.',
  past_session: 'No se puede mover una sesión que ya ha pasado.',
  past_target: 'La nueva fecha no puede ser anterior a hoy.',
  individual_or_subscription_not_movable:
    'Las sesiones individuales o de suscripción no se pueden mover en esta versión.',
  not_authorized: 'No tienes permisos para mover sesiones.',
  session_not_found: 'La sesión ya no existe.',
};

// El texto de la confirmación de "Reactivar sesión". Tiene que decir las tres
// cosas que sorprenderían a quien lo pulse esperando un "deshacer": que la
// sesión vuelve VACÍA, a cuánta gente se avisa, y que la lista de espera no
// vuelve — la cancelación la borró (mig 154) y eso no tiene marcha atrás.
export function mensajeReactivar(info: { families: number; past: boolean } | null): string {
  if (!info) return '';
  const base =
    'La sesión vuelve a la parrilla VACÍA: las inscripciones canceladas no se restauran y las familias tendrán que volver a inscribirse. ' +
    'La lista de espera NO vuelve: quien perdió su turno al cancelar no lo recupera.';
  if (info.past) {
    return `${base} La sesión ya ha pasado, así que no se avisará a nadie.`;
  }
  if (info.families === 0) {
    return `${base} No hay ninguna familia a la que avisar.`;
  }
  const n = info.families;
  return `${base} Se avisará a ${n} familia${n === 1 ? '' : 's'} de que puede volver a inscribirse.`;
}

// Nombre de cada lectura, para poder decir CUÁL no se pudo leer en vez de un
// «algo ha fallado». postgrest-js no rechaza nunca (devuelve { data: null,
// error } mientras shouldThrowOnError sea false), así que aquí el defecto
// siempre era el mismo: el `error` se tiraba y la lista vacía se leía como
// «no hay».
// Resultado de una detección de solapamientos. `comprobado: false` no es una
// lista vacía: es «no se ha podido mirar».
interface DeteccionConflictos {
  conflictos: ScheduleConflict[];
  comprobado: boolean;
}
const SIN_COMPROBAR: DeteccionConflictos = { conflictos: [], comprobado: false };

const QUE = {
  monitores: 'los entrenadores',
  grupos: 'los grupos',
  alumnos: 'los alumnos',
  especiales: 'las clases especiales',
  propuestas: 'las propuestas de tecnificación',
  inscritos: 'los inscritos',
  listaEspera: 'la lista de espera',
} as const;

export default function SesionesPage() {
  const [weekStart, setWeekStart] = useState(startOfWeek(new Date(), { weekStartsOn: 1 }));
  const [sessions, setSessions] = useState<Session[]>([]);
  const [grupos, setGrupos] = useState<Group[]>([]);
  const [specialClasses, setSpecialClasses] = useState<SpecialClassTemplate[]>([]);
  const [alumnos, setAlumnos] = useState<{ id: string; full_name: string }[]>([]);
  const [monitores, setMonitores] = useState<MonitorBrief[]>([]);
  const [loading, setLoading] = useState(true);
  const [genModal, setGenModal] = useState(false);
  const [genForm, setGenForm] = useState({
    entity: '',
    date_start: '',
    date_end: '',
    student_id: '',
    monitor_id: '',
  });
  // PR-J4: marca las sesiones generadas como elegibles para suscripción mensual.
  // Default false → el admin lo activa explícitamente al programar.
  const [allowSubscription, setAllowSubscription] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [sessionDetail, setSessionDetail] = useState<Session | null>(null);
  const [enrollments, setEnrollments] = useState<EnrollmentWithProfile[]>([]);
  const [confirmCancel, setConfirmCancel] = useState<Session | null>(null);
  const [filterMonitor, setFilterMonitor] = useState('');
  const [filterCategory, setFilterCategory] = useState('');
  // C-2: alternancia lista / agenda (rejilla horaria). La lista se conserva.
  const [viewMode, setViewMode] = useState<'list' | 'agenda'>('list');
  // C-2.1: agenda con fecha+vista CONTROLADAS. `weekStart` sigue siendo la fuente
  // de la carga de datos (semana); `agendaDate` es el día en foco del TimeGrid y
  // `agendaView` su vista. Se mantienen sincronizados con la barra de semana.
  const [agendaDate, setAgendaDate] = useState<Date>(() =>
    startOfWeek(new Date(), { weekStartsOn: 1 })
  );
  const [agendaView, setAgendaView] = useState<'day' | 'week'>('week');
  // C-4: rango horario de la agenda, preferencia por usuario (default 8–24).
  const { hourStart, hourEnd, save: saveAgendaHours, saving: savingHours } = useAgendaHours();
  // S-4: filtro multi-selección por sede (nombres; vacío = todas). Filtra en
  // cliente sobre la sede ya derivada de la sesión (sessionSede), sin queries.
  const [filterSedes, setFilterSedes] = useState<string[]>([]);
  const [editingMonitor, setEditingMonitor] = useState(false);
  const [editMonitorId, setEditMonitorId] = useState('');
  const [waitlistUsers, setWaitlistUsers] = useState<
    { user_id: string; waitlist_position: number; full_name: string; push_token: string | null }[]
  >([]);
  const [duplicateModal, setDuplicateModal] = useState(false);
  // §26: añadido `monitor_id` para permitir cambiar el entrenador al
  // duplicar. Default = monitor original de la sesión (pre-rellenado
  // al abrir el modal); editable desde el dropdown del form.
  const [dupForm, setDupForm] = useState({
    date: '',
    time_start: '',
    time_end: '',
    max_students: 0,
    monitor_id: '' as string,
  });
  const [duplicating, setDuplicating] = useState(false);
  const [duplicateError, setDuplicateError] = useState('');

  // Mover sesión (M-2): cambia fecha/hora/entrenador/sede conservando inscritos.
  const [activeSedes, setActiveSedes] = useState<Sede[]>([]);
  const [activeSedesError, setActiveSedesError] = useState<string | null>(null);

  // Listas de soporte que no se pudieron leer. Mismo criterio que
  // `activeSedesError`, que ya estaba aquí: de estas listas salen los
  // desplegables con los que se CREA y se ASIGNA, y una vacía por un fallo de
  // lectura se lee como «el club no tiene ninguno».
  const [listasFallidas, setListasFallidas] = useState<string[]>([]);
  // El detalle de una sesión: los inscritos y la lista de espera.
  const [detalleFallidos, setDetalleFallidos] = useState<string[]>([]);
  // Lo que SÍ se hizo pero no se pudo completar del todo: la acción salió
  // adelante y el aviso al alumno (o el traspaso) no. Decirlo importa porque
  // el que lo lee es el único que puede arreglarlo a mano.
  const [avisoAccion, setAvisoAccion] = useState('');
  const [moveModal, setMoveModal] = useState(false);
  const [moveForm, setMoveForm] = useState({
    date: '',
    time_start: '',
    time_end: '',
    monitor_id: '', // '' = sin asignar (hereda el del grupo)
    sede_id: '', // '' = heredar la sede del grupo
  });
  const [moving, setMoving] = useState(false);
  const [moveError, setMoveError] = useState('');

  // Añadir alumno manualmente
  const [addStudentModal, setAddStudentModal] = useState(false);
  const [addStudentId, setAddStudentId] = useState('');
  const [addStudentCourtesy, setAddStudentCourtesy] = useState(false);
  const [addingStudent, setAddingStudent] = useState(false);
  const [addStudentError, setAddStudentError] = useState('');

  // Quitar alumno
  const [removingStudentId, setRemovingStudentId] = useState<string | null>(null);

  // Modal de info médica
  // mig 281: el contenido llega al abrir, no al pintar la lista. `estado`
  // distingue «cargando», «no hay nada» y «no se pudo leer», que antes eran
  // todos el mismo texto.
  const [medicalInfoModal, setMedicalInfoModal] = useState<{
    name: string;
    info: string | null;
    estado: 'cargando' | 'ok' | 'error';
    mensaje?: string;
  } | null>(null);

  const abrirFichaMedica = useCallback(async (userId: string, nombre: string) => {
    setMedicalInfoModal({ name: nombre, info: null, estado: 'cargando' });
    const r = await leerFichaMedica(userId);
    setMedicalInfoModal(
      r.estado === 'ok'
        ? { name: nombre, info: r.ficha, estado: 'ok' }
        : { name: nombre, info: null, estado: 'error', mensaje: r.mensaje }
    );
  }, []);

  // Edición de horario y aforo
  const [editingSchedule, setEditingSchedule] = useState(false);
  const [scheduleForm, setScheduleForm] = useState({ date: '', time_start: '', time_end: '' });
  const [savingSchedule, setSavingSchedule] = useState(false);
  const [editingCapacity, setEditingCapacity] = useState(false);
  const [capacityValue, setCapacityValue] = useState(0);
  const [savingCapacity, setSavingCapacity] = useState(false);
  const [confirmDeleteSession, setConfirmDeleteSession] = useState<Session | null>(null);
  // Reactivar avisa a las familias que echó la cancelación (mig 238), así que
  // hay que saber a cuántas ANTES de pulsar: quien lo pulsa tiene que entender
  // que la sesión vuelve vacía y que se va a avisar a gente.
  const [confirmReactivate, setConfirmReactivate] = useState<{
    session: Session;
    families: number;
    past: boolean;
  } | null>(null);

  // Tecnificación individual — propuestas de horario pendientes (motor común, mig
  // 152). Se gestionan desde la agenda: al confirmar, se activa la sub y se
  // generan sus sesiones por el motor común (aforo 1 + trigger J4).
  const [pendingProposals, setPendingProposals] = useState<PendingProposal[]>([]);
  const [proposalSel, setProposalSel] = useState<PendingProposal | null>(null);
  const [proposalForm, setProposalForm] = useState({
    group_id: '',
    date_start: '',
    date_end: '',
  });
  const [confirmingProposal, setConfirmingProposal] = useState(false);
  const [proposalError, setProposalError] = useState('');
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  // C-3: vídeo-análisis AGENDADOS del club en la semana cargada (video_analysis_
  // bookings; RLS is_admin() → todos). Overlay sobre la agenda, carga por rango.
  const [videoBookings, setVideoBookings] = useState<VideoAnalysisBookingRow[]>([]);

  // Ref para acceder al sessionDetail actual desde callbacks de Realtime
  const sessionDetailRef = useRef<Session | null>(null);
  sessionDetailRef.current = sessionDetail;

  // `loadSessions` se memoiza con [weekStart] para que tanto el efecto de
  // bootstrap por semana como el handler Realtime trabajen con el closure
  // actualizado (ver comentario del useEffect Realtime abajo).
  const loadSessions = useCallback(async () => {
    const start = format(weekStart, 'yyyy-MM-dd');
    const end = format(endOfWeek(weekStart, { weekStartsOn: 1 }), 'yyyy-MM-dd');
    const { data, error } = await supabase
      .from('sessions')
      .select(
        '*, session_sede:sedes!sede_id(name), groups(name, level, class_type, sedes(name), profiles!monitor_id(id, full_name)), special_classes(name, sedes(name), profiles!monitor_id(id, full_name)), profiles!monitor_id(id, full_name)'
      )
      .gte('date', start)
      .lte('date', end)
      .order('date')
      .order('time_start');
    if (!error) {
      const rows = (data ?? []) as unknown as SessionRowWithRelations[];
      setSessions(
        rows.map<Session>((s) => ({
          ...s,
          sedeOverrideId: s.sede_id,
          sedeOverride: s.session_sede?.name ?? null,
          group: s.groups
            ? {
                name: s.groups.name,
                level: s.groups.level,
                class_type: s.groups.class_type,
                sede: s.groups.sedes?.name ?? null,
                monitor: s.groups.profiles ?? null,
              }
            : null,
          specialClass: s.special_classes
            ? {
                name: s.special_classes.name,
                sede: s.special_classes.sedes?.name ?? null,
                monitor: s.special_classes.profiles ?? null,
              }
            : null,
          monitor: s.profiles ?? null,
        }))
      );
    }
    setLoading(false);
  }, [weekStart]);

  useEffect(() => {
    loadSessions();
  }, [loadSessions]);

  // C-3: carga los vídeo-análisis agendados de la semana (mismo rango que
  // `loadSessions`). Solo en modo agenda: la lista no los muestra. Se engancha a
  // `weekStart` → al navegar de semana (barra o TimeGrid) se recargan con todo.
  const loadVideoBookings = useCallback(async () => {
    const start = format(weekStart, 'yyyy-MM-dd');
    const end = format(endOfWeek(weekStart, { weekStartsOn: 1 }), 'yyyy-MM-dd');
    const { data, error } = await supabase
      .from('video_analysis_bookings')
      .select('id, scheduled_at, status, trainer_id, user_id')
      .eq('status', 'scheduled')
      .not('scheduled_at', 'is', null)
      .gte('scheduled_at', `${start}T00:00:00`)
      .lte('scheduled_at', `${end}T23:59:59.999`);
    setVideoBookings(error || !data ? [] : (data as VideoAnalysisBookingRow[]));
  }, [weekStart]);

  useEffect(() => {
    if (viewMode === 'agenda') loadVideoBookings();
  }, [viewMode, loadVideoBookings]);
  useEffect(() => {
    loadGrupos();
    loadAlumnos();
    loadSpecialClasses();
    loadMonitores();
    loadProposals();
    void loadActiveSedes().then(({ sedes, error }) => {
      // El desplegable de sede decide a DÓNDE se mueve una sesión. Vacío y sin
      // explicación se lee como «este club no tiene sedes», y lo que toca es
      // heredar la del grupo sin saber que había otra opción.
      setActiveSedesError(error);
      setActiveSedes(sedes);
    });
    // Bootstrap único de listas de soporte (grupos/alumnos/clases especiales/
    // monitores). No dependen de estado reactivo — el contenido se refresca
    // por mutaciones del propio admin (crear/editar) que ya re-llaman aquí
    // explícitamente. Listar las funciones en deps las dispararía cada render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Auto-selección de propuesta al llegar desde la tarea del dashboard:
  // `?proposal=<subId>` apunta a una concreta; `?proposals=1` abre la más antigua.
  useEffect(() => {
    if (proposalSel || pendingProposals.length === 0) return;
    const wanted = searchParams.get('proposal');
    const openList = searchParams.get('proposals');
    if (!wanted && !openList) return;
    const target = wanted ? pendingProposals.find((p) => p.id === wanted) : pendingProposals[0];
    if (target) selectProposal(target);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pendingProposals, searchParams]);

  // Realtime: refresca la lista y el detalle cuando cambian sesiones o inscripciones.
  // Depende de `loadSessions` (memoizado con [weekStart]) → al navegar de semana,
  // el canal se re-suscribe con un closure que carga la semana actual. Antes,
  // con deps `[]`, el handler quedaba pegado al primer render y recargaba
  // siempre la semana inicial (stale closure latente).
  useEffect(() => {
    const channel = supabase
      .channel('vertex-sessions-realtime')
      .on('postgres_changes', { event: '*', schema: 'public', table: 'sessions' }, () => {
        void loadSessions();
      })
      .on('postgres_changes', { event: '*', schema: 'public', table: 'enrollments' }, async () => {
        void loadSessions();
        const detail = sessionDetailRef.current;
        if (detail) {
          const [enrollRes, waitRes] = await Promise.all([
            supabase
              .from('enrollments')
              .select('*, profiles!enrollments_user_id_fkey(full_name, phone)')
              .eq('session_id', detail.id)
              .neq('status', 'cancelled'),
            supabase.rpc('get_waitlist_users', { p_session_id: detail.id }),
          ]);
          // Igual que en `openDetail`: el refresco por realtime tampoco puede
          // dejar la lista de espera vacía sin decirlo.
          setDetalleFallidos([
            ...(enrollRes.error ? [QUE.inscritos] : []),
            ...(waitRes.error ? [QUE.listaEspera] : []),
          ]);
          const baseEnrollments = (enrollRes.data ?? []) as unknown as EnrollmentWithProfile[];
          await hydrateFlagsFicha(baseEnrollments);
          setEnrollments(baseEnrollments);
          setWaitlistUsers(waitRes.data ?? []);
        }
      })
      .subscribe();
    return () => {
      void supabase.removeChannel(channel);
    };
  }, [loadSessions]);

  // Marca (o desmarca) una lista como ilegible. Desmarcar importa: estos
  // cargadores se vuelven a llamar tras crear o editar, y un aviso que no se
  // retira cuando la lectura ya funciona es tan falso como no darlo.
  function marcarLista(que: string, fallo: boolean) {
    setListasFallidas((prev) =>
      fallo ? (prev.includes(que) ? prev : [...prev, que]) : prev.filter((q) => q !== que)
    );
  }

  async function loadMonitores() {
    const { data, error } = await supabase
      .from('profiles')
      // V-4: también los directores. `role` viene porque el desplegable los
      // agrupa aparte — mezclarlos sin distinguir sería peor que no ofrecerlos.
      .select('id, full_name, role')
      .in('role', ROLES_ASIGNABLES)
      .eq('active', true)
      .order('full_name');
    marcarLista(QUE.monitores, !!error);
    if (error) return;
    setMonitores((data ?? []) as MonitorBrief[]);
  }

  async function loadGrupos() {
    const { data, error } = await supabase
      .from('groups')
      .select(
        'id, name, class_type, time_start, time_end, days_of_week, profiles!monitor_id(id, full_name)'
      )
      .eq('active', true)
      .order('name');
    marcarLista(QUE.grupos, !!error);
    if (error) return;
    const rows = (data ?? []) as unknown as (Group & {
      profiles?: MonitorBrief | null;
    })[];
    setGrupos(
      rows.map((g) => ({
        id: g.id,
        name: g.name,
        class_type: g.class_type,
        time_start: g.time_start,
        time_end: g.time_end,
        days_of_week: g.days_of_week,
        monitor: g.profiles ?? null,
      }))
    );
  }

  // Propuestas de tecnificación individual pendientes (motor común, mig 152):
  // sub individual 'purchased' con proposed_at. Al activar/resolver, la fila pasa
  // a status='activated' y deja de contar aquí (filtro status='purchased').
  async function loadProposals() {
    const { data, error } = await supabase
      .from('user_subscriptions')
      .select(
        'id, user_id, proposed_day_of_week, proposed_time_start, proposed_at, profiles!user_id(full_name)'
      )
      .eq('class_type', 'individual')
      .eq('status', 'purchased')
      .not('proposed_at', 'is', null)
      .order('proposed_at', { ascending: true });
    // Cada propuesta es un alumno que ya PAGÓ su tecnificación individual y
    // espera hora. Si no se leen, no hay nada que avise de que están ahí.
    marcarLista(QUE.propuestas, !!error);
    if (error) return;
    const rows = (data ?? []) as unknown as {
      id: string;
      user_id: string;
      proposed_day_of_week: number | null;
      proposed_time_start: string | null;
      proposed_at: string | null;
      profiles?: { full_name: string } | null;
    }[];
    setPendingProposals(
      rows
        .filter((r) => r.proposed_day_of_week !== null && r.proposed_time_start !== null)
        .map((r) => ({
          id: r.id,
          user_id: r.user_id,
          student_name: r.profiles?.full_name ?? 'Alumno',
          proposed_day_of_week: r.proposed_day_of_week as number,
          proposed_time_start: r.proposed_time_start as string,
          proposed_at: r.proposed_at as string,
        }))
    );
  }

  async function loadSpecialClasses() {
    const { data, error } = await supabase
      .from('special_classes')
      .select('id, name, time_start, time_end, max_students, level')
      .eq('status', 'scheduled')
      .order('name');
    marcarLista(QUE.especiales, !!error);
    if (error) return;
    setSpecialClasses(data ?? []);
  }

  async function loadAlumnos() {
    const { data, error } = await supabase
      .from('profiles')
      .select('id, full_name')
      .eq('role', 'alumno')
      .eq('active', true)
      .order('full_name');
    marcarLista(QUE.alumnos, !!error);
    if (error) return;
    setAlumnos(data ?? []);
  }

  const filteredSessions = useMemo(
    () =>
      sessions.filter((s) => {
        if (filterMonitor) {
          const eff = s.monitor?.id ?? s.group?.monitor?.id ?? s.specialClass?.monitor?.id;
          if (eff !== filterMonitor) return false;
        }
        if (filterCategory) {
          const isSpecial = !!s.special_class_id || !!s.is_special_class;
          const isIndividual = !isSpecial && s.group?.class_type === 'individual';
          switch (filterCategory) {
            case 'especial':
              if (!isSpecial) return false;
              break;
            case 'individual':
              if (!isIndividual) return false;
              break;
            case 'grupal':
              if (isSpecial || isIndividual) return false;
              break;
            default:
              if (s.group?.level !== filterCategory) return false;
          }
        }
        if (filterSedes.length) {
          const isSpecial = !!s.special_class_id || !!s.is_special_class;
          const sede = isSpecial ? s.specialClass?.sede : s.group?.sede;
          if (!filterSedes.includes(sede ?? '')) return false;
        }
        return true;
      }),
    [sessions, filterMonitor, filterCategory, filterSedes]
  );

  // C-2: eventos de la AGENDA (rejilla) a partir de las sesiones YA filtradas
  // (entrenador/categoría/sede). Reutiliza `filteredSessions`; el TimeGrid es
  // agnóstico del origen. meta = la Session original → click abre su detalle.
  const agendaEvents = useMemo<TimeGridEvent[]>(
    () =>
      filteredSessions.map((s) => {
        const special = s.is_special_class || !!s.special_class_id;
        const color = special
          ? '#F59E0B'
          : s.group?.class_type === 'individual'
            ? '#9CA3AF'
            : (AGENDA_LEVEL_HEX[s.group?.level ?? ''] ?? '#FFF200');
        return {
          id: s.id,
          title: special ? (s.specialClass?.name ?? 'Clase especial') : (s.group?.name ?? '—'),
          start: new Date(`${s.date}T${s.time_start}`),
          end: new Date(`${s.date}T${s.time_end}`),
          color,
          meta: s,
        };
      }),
    [filteredSessions]
  );

  // C-3: eventos de vídeo-análisis del overlay. Respetan el filtro por ENTRENADOR
  // (el booking tiene trainer_id); el filtro por sede NO aplica (no hay columna
  // sede en el booking) y el de categoría es un atributo de sesión que no tienen.
  const videoAgendaEvents = useMemo<TimeGridEvent[]>(
    () =>
      videoBookings
        .filter((b) => !filterMonitor || b.trainer_id === filterMonitor)
        .map(videoBookingToEvent)
        .filter((e): e is TimeGridEvent => e !== null),
    [videoBookings, filterMonitor]
  );

  // Eventos que pinta la rejilla: sesiones filtradas + overlay de vídeo-análisis.
  const agendaAllEvents = useMemo<TimeGridEvent[]>(
    () => [...agendaEvents, ...videoAgendaEvents],
    [agendaEvents, videoAgendaEvents]
  );

  // C-2.1: mantener la fecha de la agenda dentro de la semana cargada. Cualquier
  // cambio de `weekStart` (barra de semana, salto a propuesta, etc.) recoloca
  // `agendaDate` al lunes de esa semana; si ya está dentro, se respeta el día en
  // foco. Update funcional → sin dependencia de `agendaDate` (no hay bucle).
  useEffect(() => {
    setAgendaDate((prev) =>
      startOfWeek(prev, { weekStartsOn: 1 }).getTime() === weekStart.getTime() ? prev : weekStart
    );
  }, [weekStart]);

  // El TimeGrid es dueño de la fecha en modo agenda: al navegar, mueve también
  // `weekStart` (que dispara la carga) si cambia de semana.
  const handleAgendaDateChange = useCallback(
    (d: Date) => {
      setAgendaDate(d);
      const ws = startOfWeek(d, { weekStartsOn: 1 });
      setWeekStart((prev) => (prev.getTime() === ws.getTime() ? prev : ws));
    },
    [setWeekStart]
  );

  const entityType = genForm.entity.startsWith('sc:') ? 'sc' : 'gr';
  const entityId = genForm.entity.includes(':') ? genForm.entity.split(':')[1] : '';
  const selectedGroup =
    entityType === 'gr' && entityId ? grupos.find((g) => g.id === entityId) : undefined;
  const selectedSC =
    entityType === 'sc' && entityId ? specialClasses.find((s) => s.id === entityId) : undefined;
  const isSpecialClass = entityType === 'sc' && !!selectedSC;

  function getEffectiveMonitor(s: Session) {
    return s.monitor ?? s.group?.monitor ?? s.specialClass?.monitor ?? null;
  }

  // ── Aviso de solapamiento de entrenador (C-2) ───────────────────────────────
  // Modal pendiente: la lista de conflictos ya detectada + la acción a ejecutar
  // si el admin decide continuar. NO bloquea (mig 162 detecta, el admin decide).
  const [conflictModal, setConflictModal] = useState<{
    conflicts: ScheduleConflict[];
    // false = no se pudo comprobar. El modal se abre igual, pero diciendo eso
    // en vez de enseñar una lista vacía de choques.
    comprobado: boolean;
    confirmLabel: string;
    onProceed: () => void | Promise<void>;
  } | null>(null);

  // Detección puntual reutilizable: resuelve la sede_id y el monitor por defecto
  // del contenedor (grupo/clase especial) con un lookup por PK, calcula el
  // entrenador EFECTIVO (override elegido, si no el del contenedor) y llama a la
  // RPC monitor_schedule_conflicts (C-0). No abre modal por sí mismo.
  //
  // ⚠️ Devolvía `[]` en TRES casos distintos: no hay conflictos, no hay
  //    entrenador posible, y no se pudo comprobar. Los dos primeros son
  //    respuestas; el tercero es silencio, y el silencio aquí significa meter
  //    al mismo entrenador en dos sitios a la vez sin un aviso. Ahora
  //    `comprobado` los separa: false = no lo sabemos, y quien llame avisa.
  async function detectSessionConflicts(params: {
    groupId: string | null;
    specialClassId: string | null;
    overrideMonitorId: string | null;
    date: string;
    timeStart: string;
    timeEnd: string;
    excludeSessionId?: string | null;
    // Sede EFECTIVA del destino (mover sesión): si se pasa, se usa tal cual como
    // p_sede_id (incluida null = sin sede). Si es `undefined`, se deriva del
    // contenedor (comportamiento de crear/duplicar/reasignar).
    overrideSedeId?: string | null;
  }): Promise<DeteccionConflictos> {
    let sedeId: string | null = null;
    let containerMonitorId: string | null = null;
    if (params.groupId) {
      const { data, error } = await supabase
        .from('groups')
        .select('sede_id, monitor_id')
        .eq('id', params.groupId)
        .single();
      // Sin la sede y el entrenador del contenedor la RPC se llamaría con los
      // datos equivocados, así que no se llama: no se ha comprobado.
      if (error) return SIN_COMPROBAR;
      sedeId = data?.sede_id ?? null;
      containerMonitorId = data?.monitor_id ?? null;
    } else if (params.specialClassId) {
      const { data, error } = await supabase
        .from('special_classes')
        .select('sede_id, monitor_id')
        .eq('id', params.specialClassId)
        .single();
      if (error) return SIN_COMPROBAR;
      sedeId = data?.sede_id ?? null;
      containerMonitorId = data?.monitor_id ?? null;
    }
    if (params.overrideSedeId !== undefined) sedeId = params.overrideSedeId;
    const effectiveMonitor = params.overrideMonitorId ?? containerMonitorId;
    // Esto SÍ es una respuesta: sin entrenador efectivo no hay con quién chocar.
    if (!effectiveMonitor) return { conflictos: [], comprobado: true };
    const { data, error } = await supabase.rpc('monitor_schedule_conflicts', {
      p_monitor_id: effectiveMonitor,
      p_date: params.date,
      p_time_start: params.timeStart,
      p_time_end: params.timeEnd,
      p_sede_id: sedeId,
      p_exclude_session_id: params.excludeSessionId ?? null,
    });
    if (error) return SIN_COMPROBAR;
    return { conflictos: (data ?? []) as unknown as ScheduleConflict[], comprobado: true };
  }

  // Detección en LOTE (C-3): un solo round-trip que expande las fechas del rango
  // con la misma lógica DOW que generate_sessions y evalúa los conflictos del
  // entrenador del grupo por fecha. Cada fila trae `date` → el modal la pinta
  // como resumen agrupado. No abre modal por sí mismo.
  async function detectGroupRangeConflicts(
    groupId: string,
    dateStart: string,
    dateEnd: string
  ): Promise<DeteccionConflictos> {
    const { data, error } = await supabase.rpc('monitor_schedule_conflicts_for_group', {
      p_group_id: groupId,
      p_date_start: dateStart,
      p_date_end: dateEnd,
    });
    if (error) return SIN_COMPROBAR;
    return { conflictos: (data ?? []) as unknown as ScheduleConflict[], comprobado: true };
  }

  // INSERT real de la clase especial de fecha suelta (extraído para poder
  // ejecutarlo directamente o tras confirmar el aviso de solapamiento).
  async function insertSpecialClassSession() {
    if (!selectedSC) return;
    setGenerating(true);
    const { error } = await supabase.from('sessions').insert({
      special_class_id: entityId,
      date: genForm.date_start,
      time_start: selectedSC.time_start,
      time_end: selectedSC.time_end,
      max_students: selectedSC.max_students,
      current_count: 0,
      status: 'scheduled',
      monitor_id: genForm.monitor_id || null,
    });
    if (!error) {
      setGenModal(false);
      await loadSessions();
    } else {
      alert(`Error: ${error.message}`);
    }
    setGenerating(false);
  }

  async function generateSessions() {
    if (!genForm.entity || !genForm.date_start) return;

    if (isSpecialClass && selectedSC) {
      // C-2: aviso de solapamiento del entrenador ANTES del INSERT puntual.
      const { conflictos, comprobado } = await detectSessionConflicts({
        groupId: null,
        specialClassId: entityId,
        overrideMonitorId: genForm.monitor_id || null,
        date: genForm.date_start,
        timeStart: selectedSC.time_start,
        timeEnd: selectedSC.time_end,
      });
      if (conflictos.length > 0 || !comprobado) {
        setConflictModal({
          conflicts: conflictos,
          comprobado,
          confirmLabel: 'Crear de todas formas',
          onProceed: insertSpecialClassSession,
        });
        return;
      }
      await insertSpecialClassSession();
      return;
    }

    // Rama de LOTE (grupo por rango).
    if (selectedGroup) {
      if (!genForm.date_end) return;
      // C-3: aviso de solapamiento en LOTE — resumen agrupado de las fechas del
      // rango en que el entrenador del grupo choca. Un solo round-trip (batch).
      const { conflictos, comprobado } = await detectGroupRangeConflicts(
        entityId,
        genForm.date_start,
        genForm.date_end
      );
      if (conflictos.length > 0 || !comprobado) {
        setConflictModal({
          conflicts: conflictos,
          comprobado,
          confirmLabel: 'Crear las sesiones de todas formas',
          onProceed: runGroupGenerate,
        });
        return;
      }
      await runGroupGenerate();
    }
  }

  // Generación real por rango (extraída para ejecutarla directamente o tras
  // confirmar el aviso de solapamiento del lote).
  async function runGroupGenerate() {
    if (!genForm.date_end) return;
    setGenerating(true);
    const { data, error } = await supabase.rpc('generate_sessions', {
      p_group_id: entityId,
      p_date_start: genForm.date_start,
      p_date_end: genForm.date_end,
      p_allow_subscription: allowSubscription,
    });
    if (!error) {
      alert(`✅ ${data} sesiones generadas`);
      setGenModal(false);
      await loadSessions();
    } else {
      alert(`Error: ${error.message}`);
    }
    setGenerating(false);
  }

  // ── Tecnificación individual: gestión de propuestas desde la agenda ──────────

  // Próxima fecha (>= hoy) cuyo DOW coincide con `dow` (0=dom..6=sáb).
  function nextDateForDow(dow: number): Date {
    const d = new Date();
    d.setHours(0, 0, 0, 0);
    for (let i = 0; i < 7; i++) {
      if (d.getDay() === dow) break;
      d.setDate(d.getDate() + 1);
    }
    return d;
  }

  // Grupo individual que casa con un día/hora (mismo criterio que resuelve
  // activate_subscription: class_type='individual', el día en days_of_week y la
  // hora de inicio). Es la unidad del motor común (entrenador + slot).
  function matchIndividualGroup(dow: number, timeStart: string): Group | undefined {
    const hhmm = timeStart.slice(0, 5);
    return grupos.find(
      (g) =>
        g.class_type === 'individual' &&
        (g.days_of_week ?? []).includes(dow) &&
        g.time_start.slice(0, 5) === hhmm
    );
  }

  function selectProposal(p: PendingProposal) {
    setProposalError('');
    setProposalSel(p);
    // Posiciona el calendario en la semana del día propuesto (para ver a ojo si
    // el entrenador está ocupado; NO hay detección automática de solape).
    const target = nextDateForDow(p.proposed_day_of_week);
    setWeekStart(startOfWeek(target, { weekStartsOn: 1 }));
    const match = matchIndividualGroup(p.proposed_day_of_week, p.proposed_time_start);
    setProposalForm({
      group_id: match?.id ?? '',
      date_start: format(target, 'yyyy-MM-dd'),
      date_end: format(endOfMonth(target), 'yyyy-MM-dd'),
    });
  }

  // Confirmar: reusa el MOTOR COMÚN (cero mecanismo nuevo). 1) activate_subscription
  // resuelve el grupo individual por día/hora y marca status='activated'. 2)
  // generate_sessions (aforo 1, allow_subscription=true) crea las sesiones sobre el
  // grupo al que quedó ligada la sub → el trigger J4 inscribe las 4 sesiones.
  async function confirmProposal() {
    if (!proposalSel) return;
    setProposalError('');
    const group = grupos.find((g) => g.id === proposalForm.group_id);
    if (!group || group.class_type !== 'individual') {
      setProposalError(
        'Elige el grupo individual (entrenador + día + hora) para esta tecnificación.'
      );
      return;
    }
    if (!proposalForm.date_start || !proposalForm.date_end) {
      setProposalError('Indica la fecha de inicio y de fin de la serie.');
      return;
    }
    if (proposalForm.date_end < proposalForm.date_start) {
      setProposalError('La fecha de fin debe ser igual o posterior a la de inicio.');
      return;
    }
    const dow = (group.days_of_week ?? [])[0];
    if (dow === undefined) {
      setProposalError('El grupo individual no tiene día configurado.');
      return;
    }

    // C-3: aviso de solapamiento del entrenador del grupo individual para el
    // rango de la serie (el motor común genera varias sesiones → batch).
    const { conflictos, comprobado } = await detectGroupRangeConflicts(
      group.id,
      proposalForm.date_start,
      proposalForm.date_end
    );
    if (conflictos.length > 0 || !comprobado) {
      setConflictModal({
        conflicts: conflictos,
        comprobado,
        confirmLabel: 'Activar de todas formas',
        onProceed: performConfirmProposal,
      });
      return;
    }
    await performConfirmProposal();
  }

  async function performConfirmProposal() {
    if (!proposalSel) return;
    const group = grupos.find((g) => g.id === proposalForm.group_id);
    const dow = group ? (group.days_of_week ?? [])[0] : undefined;
    if (!group || dow === undefined) return;
    setConfirmingProposal(true);

    // 1) Activar la sub (resuelve grupo individual + status='activated').
    const period = format(startOfMonth(parseISO(proposalForm.date_start)), 'yyyy-MM-dd');
    const { data: actData, error: actErr } = await supabase.rpc('activate_subscription', {
      p_subscription_id: proposalSel.id,
      p_period_month: period,
      p_category: null,
      p_day_of_week: dow,
      p_time_start: group.time_start,
    });
    if (actErr) {
      setConfirmingProposal(false);
      setProposalError(`No se pudo activar la suscripción: ${actErr.message}`);
      return;
    }
    const act = actData as { ok?: boolean; error?: string; group_id?: string } | null;
    if (!act?.ok) {
      setConfirmingProposal(false);
      setProposalError(
        PROPOSAL_ERRORS[act?.error ?? ''] ?? `No se pudo activar (${act?.error ?? 'desconocido'}).`
      );
      return;
    }
    // Grupo al que el motor ligó la sub: generamos sobre ÉL para que J4 case.
    const boundGroupId = act.group_id ?? group.id;

    // 2) Generar las sesiones por el motor común → trigger J4 inscribe.
    const { error: genErr } = await supabase.rpc('generate_sessions', {
      p_group_id: boundGroupId,
      p_date_start: proposalForm.date_start,
      p_date_end: proposalForm.date_end,
      p_allow_subscription: true,
    });
    setConfirmingProposal(false);
    if (genErr) {
      setProposalError(`Suscripción activada, pero fallo al generar sesiones: ${genErr.message}`);
      return;
    }
    // Resuelta: la sub pasó a 'activated' → sale de Solicitudes, entra en Activas.
    setProposalSel(null);
    searchParams.delete('proposal');
    searchParams.delete('proposals');
    setSearchParams(searchParams, { replace: true });
    await Promise.all([loadProposals(), loadSessions()]);
  }

  async function cancelSession(session: Session) {
    // Cancelación completa y consistente en una transacción (mig 154):
    // soft-cancel de la sesión + marca cancelled TODOS los enrollments (incl.
    // suscripción y sesiones a <24h) + devuelve crédito solo donde aplica +
    // vacía waitlist + notifica con el texto correcto. Sustituye al bucle frágil
    // que dejaba enrollments 'confirmed' (por el guard de crédito y el deadline).
    const { data, error } = await supabase.rpc('admin_cancel_session', {
      p_session_id: session.id,
    });
    const res = data as { ok?: boolean; error?: string } | null;
    if (error || !res?.ok) {
      alert(`Error al cancelar: ${error?.message ?? res?.error ?? 'desconocido'}`);
      return;
    }

    setSessions((prev) =>
      prev.map((x) => (x.id === session.id ? { ...x, status: 'cancelled', current_count: 0 } : x))
    );
    setSessionDetail((prev) =>
      prev && prev.id === session.id ? { ...prev, status: 'cancelled', current_count: 0 } : prev
    );
    setConfirmCancel(null);
  }

  async function openDetail(session: Session) {
    setSessionDetail(session);
    setEditingMonitor(false);
    setEditMonitorId(session.monitor_id ?? '');
    setWaitlistUsers([]);
    const [enrollRes, waitRes] = await Promise.all([
      supabase
        .from('enrollments')
        .select('*, profiles!enrollments_user_id_fkey(full_name, phone)')
        .eq('session_id', session.id)
        .neq('status', 'cancelled'),
      supabase.rpc('get_waitlist_users', { p_session_id: session.id }),
    ]);
    // ⚠️ Desde este panel se abre plaza, se duplica la clase y se traspasa a
    //    los que esperan. Una lista de espera vacía por un fallo de lectura se
    //    lee como «no hay nadie esperando», y esa es exactamente la decisión
    //    que se toma mirando aquí.
    setDetalleFallidos([
      ...(enrollRes.error ? [QUE.inscritos] : []),
      ...(waitRes.error ? [QUE.listaEspera] : []),
    ]);
    const baseEnrollments = (enrollRes.data ?? []) as unknown as EnrollmentWithProfile[];
    await hydrateFlagsFicha(baseEnrollments);
    setEnrollments(baseEnrollments);
    setWaitlistUsers(waitRes.data ?? []);
  }

  async function handleDuplicate() {
    if (!sessionDetail) return;
    setDuplicateError('');

    // mig 161: la franja es única POR ENTRENADOR (group_id, date, time_start,
    // monitor_id). Duplicar reutiliza fecha+hora del original, así que si NO se
    // cambia el entrenador chocaría con la sesión original. Avisamos ANTES del
    // INSERT en vez de dejar caer el error de constraint. Solo aplica a sesiones
    // de grupo (las especiales tienen group_id NULL y están exentas del índice).
    const chosenMonitor = dupForm.monitor_id === '' ? null : dupForm.monitor_id;
    const sameFranja =
      dupForm.date === sessionDetail.date && dupForm.time_start === sessionDetail.time_start;
    if (
      sessionDetail.group_id &&
      sameFranja &&
      chosenMonitor === (sessionDetail.monitor_id ?? null)
    ) {
      setDuplicateError(
        'Elige un entrenador distinto para duplicar: ya existe una sesión en esa franja con ese entrenador. Cambia el entrenador (o la fecha/hora) para poder atender la lista de espera con otro monitor.'
      );
      return;
    }

    // C-2: aviso de solapamiento de AGENDA del entrenador elegido (distinto y
    // adicional al guard #294 de arriba, que es el UNIQUE de franja). Detecta
    // antes del INSERT; si hay choque, el admin decide. No bloquea.
    const { conflictos, comprobado } = await detectSessionConflicts({
      groupId: sessionDetail.group_id ?? null,
      specialClassId: sessionDetail.special_class_id ?? null,
      overrideMonitorId: chosenMonitor,
      date: dupForm.date,
      timeStart: dupForm.time_start,
      timeEnd: dupForm.time_end,
    });
    if (conflictos.length > 0 || !comprobado) {
      setConflictModal({
        conflicts: conflictos,
        comprobado,
        confirmLabel: 'Duplicar de todas formas',
        onProceed: performDuplicate,
      });
      return;
    }
    await performDuplicate();
  }

  async function performDuplicate() {
    if (!sessionDetail) return;
    setDuplicating(true);

    const { data: newSession, error } = await supabase
      .from('sessions')
      .insert({
        group_id: sessionDetail.group_id ?? null,
        special_class_id: sessionDetail.special_class_id ?? null,
        is_special_class: sessionDetail.is_special_class ?? false,
        // §26: el entrenador es editable en el modal. Default = monitor
        // original (pre-rellenado al abrir el modal). El form usa string
        // vacío para "Sin asignar" — debemos tratarlo como null explícito,
        // NO usar `||` que colapsaría '' al sessionDetail.monitor_id y
        // haría imposible quitar el monitor desde el dropdown.
        monitor_id: dupForm.monitor_id === '' ? null : dupForm.monitor_id,
        date: dupForm.date,
        time_start: dupForm.time_start,
        time_end: dupForm.time_end,
        max_students: dupForm.max_students,
        current_count: 0,
        status: 'scheduled',
      })
      .select()
      .single();

    if (error || !newSession) {
      setDuplicateError(error?.message ?? 'Error al crear la sesión');
      setDuplicating(false);
      return;
    }

    // Transferir alumnos de lista de espera: auto-inscribir consumiendo crédito FIFO
    let enrolledCount = 0;
    let waitlistedCount = 0;
    if (waitlistUsers.length > 0) {
      const { data: transferred, error: errTransfer } = await supabase.rpc(
        'transfer_waitlist_to_new_session',
        {
          p_old_session_id: sessionDetail.id,
          p_new_session_id: newSession.id,
        }
      );
      // ⚠️ Lo peor de esta pantalla. Esta RPC MUEVE a los que esperan a la
      //    sesión nueva. Si falla, `transferred` llega null, el bucle no se
      //    ejecuta, los dos contadores quedan en 0 y no salta ningún aviso: el
      //    modal se cierra, la sesión está creada y el admin se va convencido
      //    de que ha atendido la lista de espera. No se ha movido nadie.
      if (errTransfer) {
        setDuplicating(false);
        setDuplicateModal(false);
        setSessionDetail(null);
        setAvisoAccion(
          `La sesión se ha creado, pero los ${waitlistUsers.length} de la lista de espera NO se han traspasado y no se les ha avisado. Tendrás que inscribirlos a mano en la sesión nueva.`
        );
        await loadSessions();
        return;
      }

      const sessionLabel = `${dupForm.date} a las ${dupForm.time_start.slice(0, 5)}`;
      for (const tu of (transferred ?? []) as {
        user_id: string;
        full_name: string;
        push_token: string | null;
        enrolled: boolean;
      }[]) {
        if (tu.enrolled) enrolledCount += 1;
        else waitlistedCount += 1;
        if (tu.push_token) {
          supabase.functions
            .invoke('gym-send-push', {
              body: {
                user_id: tu.user_id,
                title: tu.enrolled ? '¡Inscripción confirmada!' : 'Pasas a lista de espera',
                body: tu.enrolled
                  ? `Te hemos inscrito automáticamente en la clase del ${sessionLabel}.`
                  : `Se ha abierto una nueva clase el ${sessionLabel} pero no tienes bono. Recarga para optar.`,
                type: 'waitlist',
                data: { screen: 'MySessions', session_id: newSession.id },
              },
            })
            .catch(() => {});
        }
      }
    }

    setDuplicating(false);
    setDuplicateModal(false);
    setSessionDetail(null);
    if (enrolledCount > 0 || waitlistedCount > 0) {
      const parts: string[] = [];
      if (enrolledCount > 0)
        parts.push(`${enrolledCount} inscrito${enrolledCount === 1 ? '' : 's'}`);
      if (waitlistedCount > 0) parts.push(`${waitlistedCount} en lista de espera (sin bono)`);
      alert(`Nueva sesión creada. ${parts.join(' · ')}. Notificaciones enviadas.`);
    }
    await loadSessions();
  }

  // ── Mover sesión (M-2 + N-1) ──────────────────────────────────────────────
  // Movible cualquier sesión PROGRAMADA y FUTURA, de cualquier tipo: grupal,
  // especial, individual y de suscripción (allow_subscription). N-1 (mig 165)
  // levantó la restricción de individual/suscripción: mover es un UPDATE que
  // conserva inscritos y no re-dispara el motor J4; el RPC resetea el aviso de
  // caducidad. Los mismos guards (scheduled/futura/slot_taken) siguen en el RPC.
  function canMoveSession(s: Session): boolean {
    if (s.status !== 'scheduled') return false;
    if (s.date < format(new Date(), 'yyyy-MM-dd')) return false;
    return true;
  }

  function openMove() {
    if (!sessionDetail) return;
    setMoveError('');
    setMoveForm({
      date: sessionDetail.date,
      time_start: sessionDetail.time_start.slice(0, 5),
      time_end: sessionDetail.time_end.slice(0, 5),
      monitor_id: sessionDetail.monitor_id ?? '',
      sede_id: sessionDetail.sedeOverrideId ?? '',
    });
    setMoveModal(true);
  }

  async function handleMove() {
    if (!sessionDetail) return;
    setMoveError('');
    if (!moveForm.date || !moveForm.time_start || !moveForm.time_end) {
      setMoveError('Indica fecha, hora de inicio y hora de fin.');
      return;
    }
    const chosenMonitor = moveForm.monitor_id === '' ? null : moveForm.monitor_id;
    const chosenSede = moveForm.sede_id === '' ? null : moveForm.sede_id;
    // Aviso de solapamiento del entrenador efectivo en el DESTINO (C-2). Excluye
    // la propia sesión (excludeSessionId) para no detectarse a sí misma. Si se
    // hereda la sede (chosenSede null) se pasa overrideSedeId undefined → la
    // detección la deriva del grupo (sede efectiva heredada).
    const { conflictos, comprobado } = await detectSessionConflicts({
      groupId: sessionDetail.group_id ?? null,
      specialClassId: sessionDetail.special_class_id ?? null,
      overrideMonitorId: chosenMonitor,
      date: moveForm.date,
      timeStart: moveForm.time_start,
      timeEnd: moveForm.time_end,
      excludeSessionId: sessionDetail.id,
      overrideSedeId: chosenSede === null ? undefined : chosenSede,
    });
    if (conflictos.length > 0 || !comprobado) {
      setConflictModal({
        conflicts: conflictos,
        comprobado,
        confirmLabel: 'Mover de todas formas',
        onProceed: performMove,
      });
      return;
    }
    await performMove();
  }

  async function performMove() {
    if (!sessionDetail) return;
    setConflictModal(null);
    setMoving(true);
    const chosenMonitor = moveForm.monitor_id === '' ? null : moveForm.monitor_id;
    const chosenSede = moveForm.sede_id === '' ? null : moveForm.sede_id;
    const movedId = sessionDetail.id;
    const { data, error } = await supabase.rpc('admin_move_session', {
      p_session_id: movedId,
      p_date: moveForm.date,
      p_time_start: moveForm.time_start,
      p_time_end: moveForm.time_end,
      p_monitor_id: chosenMonitor,
      p_sede_id: chosenSede,
    });
    const res = (data ?? null) as {
      ok: boolean;
      error?: string;
      notified?: number;
      user_ids?: string[];
    } | null;
    if (error || !res) {
      setMoveError(error?.message ?? 'Error al mover la sesión.');
      setMoving(false);
      return;
    }
    if (!res.ok) {
      // slot_taken (colisión UNIQUE mig 161) y demás códigos → error BLOQUEANTE:
      // el form NO se cierra para que el admin corrija.
      setMoveError(MOVE_ERRORS[res.error ?? ''] ?? 'No se pudo mover la sesión.');
      setMoving(false);
      return;
    }
    // El RPC ya insertó la notif in-app; aquí solo el push por user_id
    // (gym-send-push no re-inserta in-app). Patrón performDuplicate.
    const sedeName = chosenSede
      ? (activeSedes.find((s) => s.id === chosenSede)?.name ?? null)
      : (sessionDetail.group?.sede ?? sessionDetail.specialClass?.sede ?? null);
    const pushBody = `Tu sesión se ha movido al ${format(parseISO(moveForm.date), 'dd/MM/yyyy')} de ${moveForm.time_start.slice(0, 5)} a ${moveForm.time_end.slice(0, 5)}${sedeName ? ` en ${sedeName}` : ''}.`;
    for (const uid of res.user_ids ?? []) {
      supabase.functions
        .invoke('gym-send-push', {
          body: {
            user_id: uid,
            title: 'Sesión movida',
            body: pushBody,
            type: 'session_moved',
            data: { screen: 'MySessions', session_id: movedId },
          },
        })
        .catch(() => {});
    }
    setMoving(false);
    setMoveModal(false);
    setSessionDetail(null);
    const n = res.notified ?? 0;
    alert(`Sesión movida. ${n} jugador${n === 1 ? '' : 'es'} notificado${n === 1 ? '' : 's'}.`);
    await loadSessions();
  }

  async function handleAddStudent() {
    if (!sessionDetail || !addStudentId) return;
    setAddingStudent(true);
    setAddStudentError('');

    const { data, error } = await supabase.rpc('staff_add_student_to_session', {
      p_student_id: addStudentId,
      p_session_id: sessionDetail.id,
      p_consume_credit: !addStudentCourtesy,
    });

    setAddingStudent(false);

    const result = data as StaffAddResult | null;
    if (error || !result?.ok) {
      const message = result && !result.ok ? result.error : error?.message;
      setAddStudentError(message ?? 'Error al inscribir alumno');
      return;
    }

    const { data: profileData, error: errPush } = await supabase
      .from('profiles')
      .select('push_token')
      .eq('id', addStudentId)
      .single();
    // La inscripción YA está hecha. Lo único que se pierde aquí es el aviso, y
    // eso es lo que hay que decir: quien lo lee es el único que puede llamarle.
    if (errPush) {
      setAvisoAccion(
        'El alumno queda inscrito, pero no se ha podido comprobar si tiene la app para avisarle. No des por hecho que lo sabe.'
      );
    }
    const pushToken = (profileData as { push_token: string | null } | null)?.push_token;
    if (pushToken) {
      supabase.functions
        .invoke('gym-send-push', {
          body: {
            user_id: addStudentId,
            title: 'Inscripción confirmada',
            body: `Has sido inscrito en la clase del ${format(parseISO(sessionDetail.date), 'dd/MM/yyyy')} a las ${sessionDetail.time_start.slice(0, 5)}.`,
            type: 'enrollment',
            data: { screen: 'MySessions', session_id: sessionDetail.id },
          },
        })
        .catch(() => {});
    }

    setAddStudentModal(false);
    setAddStudentId('');
    setAddStudentCourtesy(false);
    await openDetail(sessionDetail);
  }

  async function handleRemoveStudent(studentId: string, studentName: string) {
    if (!sessionDetail) return;
    if (
      !confirm(
        `¿Dar de baja a ${studentName} de esta sesión? Si tenía bono asignado, se le devolverá la sesión.`
      )
    )
      return;

    setRemovingStudentId(studentId);
    const { data, error } = await supabase.rpc('staff_remove_student_from_session', {
      p_student_id: studentId,
      p_session_id: sessionDetail.id,
    });
    setRemovingStudentId(null);

    const result = data as StaffRemoveResult | null;
    if (error || !result?.ok) {
      const message = result && !result.ok ? result.error : error?.message;
      alert(message ?? 'Error al dar de baja');
      return;
    }

    if (result.next_promoted_user_id && result.next_promoted_push_token) {
      supabase.functions
        .invoke('gym-send-push', {
          body: {
            user_id: result.next_promoted_user_id,
            title: '¡Plaza confirmada!',
            body: `Has sido inscrito automáticamente en la sesión del ${format(parseISO(sessionDetail.date), 'dd/MM/yyyy')} a las ${sessionDetail.time_start.slice(0, 5)}.`,
            type: 'waitlist',
            data: { screen: 'MySessions', session_id: sessionDetail.id },
          },
        })
        .catch(() => {});
    }

    await openDetail(sessionDetail);
  }

  // Marca o desmarca asistencia de un enrollment. Click sobre el mismo
  // estado (ya marcado "Asistió" y vuelves a pulsar "Asistió") lo limpia a
  // null — mismo patrón que el monitor en MonitorSessionDetailPage. Update
  // directo a `enrollments.attendance` (policy "admin puede todo" 018).
  async function handleSetAttendance(enrollmentId: string, value: boolean) {
    const current = enrollments.find((row) => row.id === enrollmentId)?.attendance ?? null;
    const next = current === value ? null : value;
    setEnrollments((prev) =>
      prev.map((row) => (row.id === enrollmentId ? { ...row, attendance: next } : row))
    );
    const { error } = await supabase
      .from('enrollments')
      .update({ attendance: next })
      .eq('id', enrollmentId);
    if (error) {
      alert(error.message);
      // Rollback optimistic update on failure.
      setEnrollments((prev) =>
        prev.map((row) => (row.id === enrollmentId ? { ...row, attendance: current } : row))
      );
    }
  }

  async function saveSessionMonitor() {
    if (!sessionDetail) return;
    // C-2: aviso de solapamiento del NUEVO entrenador para la fecha/hora/sede de
    // esta sesión, EXCLUYÉNDOLA (p_exclude_session_id) para no compararla consigo
    // misma. No bloquea: el admin decide si reasigna igualmente.
    const { conflictos, comprobado } = await detectSessionConflicts({
      groupId: sessionDetail.group_id ?? null,
      specialClassId: sessionDetail.special_class_id ?? null,
      overrideMonitorId: editMonitorId || null,
      date: sessionDetail.date,
      timeStart: sessionDetail.time_start,
      timeEnd: sessionDetail.time_end,
      excludeSessionId: sessionDetail.id,
    });
    if (conflictos.length > 0 || !comprobado) {
      setConflictModal({
        conflicts: conflictos,
        comprobado,
        confirmLabel: 'Reasignar de todas formas',
        onProceed: performSaveMonitor,
      });
      return;
    }
    await performSaveMonitor();
  }

  async function performSaveMonitor() {
    if (!sessionDetail) return;
    const { error } = await supabase
      .from('sessions')
      .update({ monitor_id: editMonitorId || null })
      .eq('id', sessionDetail.id);
    if (error) {
      alert(error.message);
      return;
    }
    const newMonitor = monitores.find((t) => t.id === editMonitorId) ?? null;
    setSessions((prev) =>
      prev.map((s) =>
        s.id === sessionDetail.id
          ? { ...s, monitor_id: editMonitorId || null, monitor: newMonitor }
          : s
      )
    );
    setSessionDetail((prev) =>
      prev ? { ...prev, monitor_id: editMonitorId || null, monitor: newMonitor } : null
    );
    setEditingMonitor(false);
  }

  async function saveSchedule() {
    if (!sessionDetail) return;
    if (!scheduleForm.date || !scheduleForm.time_start || !scheduleForm.time_end) return;
    setSavingSchedule(true);
    const { error } = await supabase
      .from('sessions')
      .update({
        date: scheduleForm.date,
        time_start: scheduleForm.time_start,
        time_end: scheduleForm.time_end,
      })
      .eq('id', sessionDetail.id);
    setSavingSchedule(false);
    if (error) {
      alert(error.message);
      return;
    }
    setSessionDetail((prev) =>
      prev
        ? {
            ...prev,
            date: scheduleForm.date,
            time_start: scheduleForm.time_start,
            time_end: scheduleForm.time_end,
          }
        : null
    );
    setSessions((prev) =>
      prev.map((s) =>
        s.id === sessionDetail.id
          ? {
              ...s,
              date: scheduleForm.date,
              time_start: scheduleForm.time_start,
              time_end: scheduleForm.time_end,
            }
          : s
      )
    );
    setEditingSchedule(false);
  }

  async function saveCapacity() {
    if (!sessionDetail) return;
    if (capacityValue < 1) {
      alert('Aforo mínimo: 1');
      return;
    }
    setSavingCapacity(true);
    const { data, error } = await supabase.rpc('update_session_capacity', {
      p_session_id: sessionDetail.id,
      p_new_max: capacityValue,
    });
    setSavingCapacity(false);
    const result = data as UpdateCapacityResult | null;
    if (error || !result?.ok) {
      const message = result && !result.ok ? result.error : error?.message;
      alert(message ?? 'No se pudo actualizar el aforo');
      return;
    }
    setEditingCapacity(false);
    if ((result.promoted ?? 0) > 0) {
      alert(
        `Aforo actualizado. ${result.promoted} alumno(s) inscrito(s) automáticamente desde lista de espera.`
      );
    }
    await openDetail(sessionDetail);
    await loadSessions();
  }

  // Lo que se enseña antes de pulsar. Sale de la misma consulta que manda los
  // avisos (mig 238), para que el número prometido y el enviado sean el mismo.
  async function askReactivateSession() {
    if (!sessionDetail) return;
    const { data, error } = await supabase.rpc('admin_reactivate_session_preview', {
      p_session_id: sessionDetail.id,
    });
    const res = data as { ok?: boolean; error?: string; families?: number; past?: boolean } | null;
    if (error || !res?.ok) {
      alert(
        `No se pudo preparar la reactivación: ${error?.message ?? res?.error ?? 'desconocido'}`
      );
      return;
    }
    setConfirmReactivate({
      session: sessionDetail,
      families: res.families ?? 0,
      past: res.past ?? false,
    });
  }

  async function reactivateSession(session: Session) {
    // Reactivar NO restaura nada: la sesión vuelve vacía y se avisa a las
    // familias para que se reinscriban (mig 238). Antes esto era un
    // `update({ status: 'scheduled' })` a pelo, que devolvía la sesión a la
    // parrilla sin decírselo a nadie.
    const { data, error } = await supabase.rpc('admin_reactivate_session', {
      p_session_id: session.id,
    });
    const res = data as { ok?: boolean; error?: string; notified?: number } | null;
    if (error || !res?.ok) {
      alert(`Error al reactivar: ${error?.message ?? res?.error ?? 'desconocido'}`);
      return;
    }
    setSessionDetail((prev) => (prev ? { ...prev, status: 'scheduled' } : null));
    setConfirmReactivate(null);
    await loadSessions();
    if ((res.notified ?? 0) > 0) {
      alert(
        `Sesión reabierta. Se ha avisado a ${res.notified} familia(s) de que puede volver a inscribirse.`
      );
    }
  }

  async function deleteSession(s: Session) {
    const { error } = await supabase.from('sessions').delete().eq('id', s.id);
    if (error) {
      // [350] El guard de `sessions` salta aquí si la sesión tiene
      // inscripciones pagadas vivas. Su texto crudo nombra una función de SQL;
      // `mensajeDelGuard` dice el botón. Si el error es otro, se enseña tal
      // cual, que es lo que hacía antes.
      alert(mensajeDelGuard(error) ?? error.message);
      return;
    }
    setSessions((prev) => prev.filter((x) => x.id !== s.id));
    setSessionDetail(null);
    setConfirmDeleteSession(null);
  }

  function sessionLabel(s: Session): string {
    if (s.special_class_id || s.is_special_class) return s.specialClass?.name ?? 'Clase especial';
    return s.group?.name ?? '—';
  }

  // Sede EFECTIVA: el override de la sesión (sessions.sede_id, mig 163) gana; si
  // no hay override, hereda del contenedor (grupo o clase especial). Espeja el
  // COALESCE(sessions.sede_id, groups.sede_id, special_classes.sede_id) del RPC.
  function sessionSede(s: Session): string | null {
    if (s.sedeOverride) return s.sedeOverride;
    if (s.special_class_id || s.is_special_class) return s.specialClass?.sede ?? null;
    return s.group?.sede ?? null;
  }

  function sessionBorderColor(s: Session): string {
    if (s.special_class_id || s.is_special_class)
      return 'bg-warning/10 border-warning/40 hover:border-warning';
    if (s.group?.class_type === 'individual')
      return 'bg-silver/5 border-silver/40 hover:border-silver';
    const level = s.group?.level ?? '';
    return `bg-bg-surface ${LEVEL_COLORS[level] ?? 'border-bg-border'}`;
  }

  const days = Array.from({ length: 7 }, (_, i) => addDays(weekStart, i));

  return (
    <div className="space-y-6">
      {/* Lo que no se ha podido leer, dicho por su nombre. Va arriba porque de
          estas listas salen los desplegables con los que se crea y se asigna:
          una vacía sin explicación se lee como «no hay ninguno». */}
      {listasFallidas.length > 0 && (
        <div
          className="card border-error/40 bg-error/5"
          data-testid="sesiones-listas-fallidas"
          role="status"
        >
          <p className="text-error text-sm">
            No se han podido cargar {listaEnCastellano([...listasFallidas])}. Lo que salga vacío
            abajo no significa que no exista.
          </p>
        </div>
      )}

      {/* Una acción que salió adelante a medias. No es un error de la acción:
          es lo que queda por hacer a mano. */}
      {avisoAccion && (
        <div
          className="card border-warning/40 bg-warning/5"
          data-testid="sesiones-aviso-accion"
          role="status"
        >
          <p className="text-warning text-sm">{avisoAccion}</p>
          <button onClick={() => setAvisoAccion('')} className="btn-ghost text-xs mt-2">
            Entendido
          </button>
        </div>
      )}

      {/* Barra superior */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <button
            onClick={() => setWeekStart(subWeeks(weekStart, 1))}
            className="btn-ghost p-2"
            data-testid="agenda-prev-week"
          >
            <ChevronLeft size={16} />
          </button>
          <p
            className="font-oswald text-white uppercase tracking-wider"
            data-testid="agenda-week-label"
            data-week-start={format(weekStart, 'yyyy-MM-dd')}
          >
            {format(weekStart, "d 'de' MMMM", { locale: es })} —{' '}
            {format(endOfWeek(weekStart, { weekStartsOn: 1 }), "d 'de' MMMM yyyy", { locale: es })}
          </p>
          <button
            onClick={() => setWeekStart(addWeeks(weekStart, 1))}
            className="btn-ghost p-2"
            data-testid="agenda-next-week"
          >
            <ChevronRight size={16} />
          </button>
          <button
            onClick={() => setWeekStart(startOfWeek(new Date(), { weekStartsOn: 1 }))}
            className="btn-ghost text-sm px-3 py-1.5"
            data-testid="agenda-today"
          >
            Hoy
          </button>
        </div>
        <button
          onClick={() => {
            setGenForm({
              entity: '',
              date_start: '',
              date_end: '',
              student_id: '',
              monitor_id: '',
            });
            setAllowSubscription(false);
            setGenModal(true);
          }}
          data-testid="gen-sessions-btn"
          className="btn-gold flex items-center gap-2"
        >
          <Zap size={16} /> Programar sesiones
        </button>
      </div>

      {/* Propuestas de tecnificación individual pendientes (motor común, mig 152).
          El admin las gestiona aquí: el calendario sigue visible debajo para ver a
          ojo si el entrenador está libre. NO hay detección automática de solape. */}
      {pendingProposals.length > 0 && (
        <div className="card border-gold-primary/40" data-testid="proposals-panel">
          <div className="flex items-center gap-2 mb-3">
            <CalendarClock size={16} className="text-gold-primary" />
            <h3 className="font-oswald text-white uppercase tracking-wide text-sm">
              Propuestas de tecnificación individual ({pendingProposals.length})
            </h3>
          </div>
          <div className="space-y-2">
            {pendingProposals.map((p) => (
              <div
                key={p.id}
                data-testid={`proposal-row-${p.id}`}
                className={`flex items-center justify-between gap-3 rounded-lg border p-3 ${
                  proposalSel?.id === p.id
                    ? 'border-gold-primary bg-gold-primary/10'
                    : 'border-bg-border bg-bg-elevated'
                }`}
              >
                <p className="text-sm text-white font-inter">
                  Propuesta de <span className="font-medium">{p.student_name}</span>:{' '}
                  {DAY_LABELS[p.proposed_day_of_week]} {p.proposed_time_start.slice(0, 5)}
                </p>
                <button
                  onClick={() => selectProposal(p)}
                  data-testid={`proposal-manage-${p.id}`}
                  className="btn-gold text-sm px-3 py-1.5 flex-shrink-0"
                >
                  Gestionar
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Gestión de la propuesta seleccionada (inline, para no tapar el calendario).
          Confirmar reusa el MOTOR COMÚN: activate_subscription + generate_sessions
          (aforo 1) → el trigger J4 inscribe las 4 sesiones. */}
      {proposalSel && (
        <div className="card border-gold-primary" data-testid="proposal-manage-panel">
          <div className="flex items-center justify-between mb-3">
            <p className="font-oswald text-white uppercase tracking-wide text-sm flex items-center gap-2">
              <CalendarClock size={16} className="text-gold-primary" />
              Propuesta de {proposalSel.student_name}:{' '}
              {DAY_LABELS[proposalSel.proposed_day_of_week]}{' '}
              {proposalSel.proposed_time_start.slice(0, 5)}
            </p>
            <button
              onClick={() => {
                setProposalSel(null);
                setProposalError('');
              }}
              className="btn-ghost text-sm px-3 py-1.5"
              data-testid="proposal-cancel"
            >
              Cerrar
            </button>
          </div>
          <p className="text-text-secondary text-xs font-inter mb-3">
            Elige el grupo individual (entrenador · día · hora) y el rango. Al confirmar se crean
            las sesiones por el motor común (aforo 1) y el alumno queda inscrito en sus 4 sesiones.
            Puedes cambiar de grupo si su horario propuesto está ocupado.
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div className="sm:col-span-3">
              <label className="label">Grupo individual (entrenador · día · hora)</label>
              <select
                className="input"
                data-testid="proposal-group"
                value={proposalForm.group_id}
                onChange={(e) => setProposalForm((f) => ({ ...f, group_id: e.target.value }))}
              >
                <option value="">Selecciona…</option>
                {grupos
                  .filter((g) => g.class_type === 'individual')
                  .map((g) => (
                    <option key={g.id} value={g.id}>
                      {g.name} · {(g.days_of_week ?? []).map((d) => DAY_LABELS[d]).join('/')}{' '}
                      {g.time_start.slice(0, 5)}
                      {g.monitor ? ` · ${g.monitor.full_name}` : ''}
                    </option>
                  ))}
              </select>
            </div>
            <div>
              <label className="label">Fecha inicio *</label>
              <input
                type="date"
                className="input"
                data-testid="proposal-date-start"
                value={proposalForm.date_start}
                onChange={(e) => setProposalForm((f) => ({ ...f, date_start: e.target.value }))}
              />
            </div>
            <div>
              <label className="label">Fecha fin *</label>
              <input
                type="date"
                className="input"
                data-testid="proposal-date-end"
                value={proposalForm.date_end}
                onChange={(e) => setProposalForm((f) => ({ ...f, date_end: e.target.value }))}
              />
            </div>
            <div className="flex items-end">
              <button
                onClick={confirmProposal}
                disabled={confirmingProposal || !proposalForm.group_id}
                data-testid="proposal-confirm"
                className="btn-gold w-full flex items-center justify-center gap-2 disabled:opacity-50"
              >
                {confirmingProposal ? 'Confirmando…' : 'Confirmar y crear sesiones'}
              </button>
            </div>
          </div>
          {proposalError && (
            <p className="text-error text-sm mt-3" data-testid="proposal-error">
              {proposalError}
            </p>
          )}
        </div>
      )}

      {/* Filtros */}
      <div className="flex items-center gap-3 flex-wrap">
        <Filter size={14} className="text-text-secondary flex-shrink-0" />
        <select
          className="input py-1.5 text-sm w-auto min-w-0"
          value={filterMonitor}
          onChange={(e) => setFilterMonitor(e.target.value)}
        >
          <option value="">Todos los entrenadores</option>
          <OpcionesDeEntrenador lista={monitores} />
        </select>
        <select
          className="input py-1.5 text-sm w-auto min-w-0"
          value={filterCategory}
          onChange={(e) => setFilterCategory(e.target.value)}
        >
          <option value="">Todas las categorías</option>
          <option value="grupal">Grupal</option>
          <option value="individual">Individual</option>
          <option value="especial">Especial</option>
          <option value="basico">Básico</option>
          <option value="medio">Medio</option>
          <option value="avanzado">Avanzado</option>
          <option value="tecnificacion">Tecnificación</option>
        </select>
        <SedeFilter selected={filterSedes} onChange={setFilterSedes} />
        {(filterMonitor || filterCategory || filterSedes.length > 0) && (
          <button
            onClick={() => {
              setFilterMonitor('');
              setFilterCategory('');
              setFilterSedes([]);
            }}
            className="text-text-secondary hover:text-gold-primary text-sm transition-colors"
          >
            Limpiar filtros
          </button>
        )}
      </div>

      {/* Leyenda */}
      <div className="flex items-center gap-4 text-xs text-text-secondary">
        <span className="flex items-center gap-1">
          <span className="w-3 h-3 rounded border border-gold-primary/50 bg-bg-surface inline-block" />{' '}
          Grupal
        </span>
        <span className="flex items-center gap-1">
          <span className="w-3 h-3 rounded border border-silver/40 bg-silver/5 inline-block" />
          <User size={9} className="text-silver" /> Individual
        </span>
        <span className="flex items-center gap-1">
          <Star size={10} className="text-warning" /> Especial
        </span>
      </div>

      {/* C-2: alternar lista (rejilla semanal de tarjetas) / agenda (TimeGrid). */}
      <div className="flex gap-1 bg-bg-surface border border-bg-border rounded-xl p-1 w-fit">
        <button
          onClick={() => setViewMode('list')}
          data-testid="sesiones-view-list"
          className={`px-4 py-1.5 rounded-lg text-sm font-inter flex items-center gap-2 transition-colors ${
            viewMode === 'list'
              ? 'bg-gold-primary text-bg-primary font-semibold'
              : 'text-text-secondary hover:text-white'
          }`}
        >
          <List size={14} /> Lista
        </button>
        <button
          onClick={() => setViewMode('agenda')}
          data-testid="sesiones-view-agenda"
          className={`px-4 py-1.5 rounded-lg text-sm font-inter flex items-center gap-2 transition-colors ${
            viewMode === 'agenda'
              ? 'bg-gold-primary text-bg-primary font-semibold'
              : 'text-text-secondary hover:text-white'
          }`}
        >
          <LayoutGrid size={14} /> Agenda
        </button>
      </div>

      {/* C-4: ajuste del rango horario (solo en agenda). Guardar refleja al instante. */}
      {viewMode === 'agenda' && (
        <AgendaHoursControl
          start={hourStart}
          end={hourEnd}
          onChange={saveAgendaHours}
          disabled={savingHours}
        />
      )}

      {/* Calendario semanal (lista) / Agenda (rejilla horaria) */}
      {viewMode === 'agenda' ? (
        <TimeGrid
          events={agendaAllEvents}
          date={agendaDate}
          onDateChange={handleAgendaDateChange}
          view={agendaView}
          onViewChange={setAgendaView}
          hourStart={hourStart}
          hourEnd={hourEnd}
          onEventClick={(e) =>
            isVideoAnalysisEvent(e)
              ? navigate(`/video-analisis?booking=${getVideoBookingId(e)}`)
              : openDetail(e.meta as Session)
          }
        />
      ) : loading ? (
        <div className="flex items-center justify-center h-64">
          <div className="w-8 h-8 border-2 border-gold-primary/30 border-t-gold-primary rounded-full animate-spin" />
        </div>
      ) : (
        <div className="grid grid-cols-7 gap-2">
          {days.map((day) => {
            const daySessions = filteredSessions.filter((s) => isSameDay(parseISO(s.date), day));
            const isToday = isSameDay(day, new Date());
            return (
              <div key={day.toISOString()} className="min-h-32">
                <div
                  className={`text-center py-2 rounded-t-lg mb-2 ${isToday ? 'bg-gold-primary text-bg-primary' : 'bg-bg-elevated text-text-secondary'}`}
                >
                  <p className="text-xs font-inter uppercase">
                    {format(day, 'EEE', { locale: es })}
                  </p>
                  <p className="font-oswald text-lg">{format(day, 'd')}</p>
                </div>
                <div className="space-y-1">
                  {daySessions.map((s) => {
                    const isSpecial = s.is_special_class || !!s.special_class_id;
                    const isInd = s.group?.class_type === 'individual';
                    const effectiveMonitor = getEffectiveMonitor(s);
                    return (
                      <button
                        key={s.id}
                        onClick={() => openDetail(s)}
                        data-testid={`session-card-${s.id}`}
                        className={`w-full text-left p-2 rounded border text-xs transition-all ${
                          s.status === 'cancelled'
                            ? 'opacity-50 border-error/30 bg-error/5'
                            : sessionBorderColor(s)
                        }`}
                      >
                        <div className="flex items-center gap-1 mb-0.5">
                          {isSpecial && <Star size={9} className="text-warning flex-shrink-0" />}
                          {isInd && !isSpecial && (
                            <User size={9} className="text-silver flex-shrink-0" />
                          )}
                          <p className="font-inter font-medium text-white truncate">
                            {sessionLabel(s)}
                          </p>
                        </div>
                        <p className="text-text-secondary">
                          {s.time_start.slice(0, 5)}–{s.time_end.slice(0, 5)}
                        </p>
                        <p
                          className={
                            s.current_count >= s.max_students ? 'text-error' : 'text-text-secondary'
                          }
                        >
                          {s.current_count}/{s.max_students}
                          {s.current_count >= s.max_students && (
                            <span className="ml-1 text-amber-400">⏳</span>
                          )}
                        </p>
                        {effectiveMonitor && (
                          <p className="text-text-secondary truncate mt-0.5 opacity-70">
                            {effectiveMonitor.full_name}
                          </p>
                        )}
                        {sessionSede(s) && (
                          <p className="text-text-secondary truncate mt-0.5 opacity-70 flex items-center gap-0.5">
                            <MapPin size={9} className="flex-shrink-0" />
                            {sessionSede(s)}
                          </p>
                        )}
                      </button>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Modal programar sesiones */}
      <Modal open={genModal} onClose={() => setGenModal(false)} title="Programar Sesiones">
        <div className="space-y-4">
          <div>
            <label className="label">Seleccionar sesión *</label>
            <select
              className="input"
              value={genForm.entity}
              onChange={(e) =>
                setGenForm((f) => ({
                  ...f,
                  entity: e.target.value,
                  student_id: '',
                  monitor_id: '',
                }))
              }
            >
              <option value="">Seleccionar...</option>
              {grupos.filter((g) => g.class_type !== 'individual').length > 0 && (
                <>
                  <option disabled>── Grupales ──</option>
                  {grupos
                    .filter((g) => g.class_type !== 'individual')
                    .map((g) => (
                      <option key={g.id} value={`gr:${g.id}`}>
                        {g.name}
                      </option>
                    ))}
                </>
              )}
              {grupos.filter((g) => g.class_type === 'individual').length > 0 && (
                <>
                  <option disabled>── Individuales ──</option>
                  {grupos
                    .filter((g) => g.class_type === 'individual')
                    .map((g) => (
                      <option key={g.id} value={`gr:${g.id}`}>
                        {g.name}
                      </option>
                    ))}
                </>
              )}
              {specialClasses.length > 0 && (
                <>
                  <option disabled>── Clases Especiales ──</option>
                  {specialClasses.map((sc) => (
                    <option key={sc.id} value={`sc:${sc.id}`}>
                      {sc.name}
                    </option>
                  ))}
                </>
              )}
            </select>
          </div>

          {isSpecialClass && (
            <div>
              <label className="label">Fecha *</label>
              <input
                type="date"
                className="input"
                value={genForm.date_start}
                onChange={(e) => setGenForm((f) => ({ ...f, date_start: e.target.value }))}
              />
            </div>
          )}

          {isSpecialClass && (
            <div>
              <label className="label">Entrenador</label>
              <select
                className="input"
                value={genForm.monitor_id}
                onChange={(e) => setGenForm((f) => ({ ...f, monitor_id: e.target.value }))}
              >
                <option value="">Sin asignar</option>
                <OpcionesDeEntrenador lista={monitores} />
              </select>
            </div>
          )}

          {selectedGroup && (
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="label">Fecha inicio *</label>
                <input
                  type="date"
                  className="input"
                  value={genForm.date_start}
                  onChange={(e) => setGenForm((f) => ({ ...f, date_start: e.target.value }))}
                />
              </div>
              <div>
                <label className="label">Fecha fin *</label>
                <input
                  type="date"
                  className="input"
                  value={genForm.date_end}
                  onChange={(e) => setGenForm((f) => ({ ...f, date_end: e.target.value }))}
                />
              </div>
            </div>
          )}

          {selectedGroup && (
            <label className="flex items-start gap-2 text-sm text-white cursor-pointer">
              <input
                type="checkbox"
                data-testid="gen-allow-subscription"
                checked={allowSubscription}
                onChange={(e) => setAllowSubscription(e.target.checked)}
                className="w-4 h-4 mt-0.5 accent-gold-primary"
              />
              <span>
                Permitir suscripción mensual en estas sesiones
                <span className="block text-text-secondary text-xs">
                  Los alumnos con suscripción activa de esta categoría se inscribirán
                  automáticamente.
                </span>
              </span>
            </label>
          )}

          <div className="flex justify-end gap-3">
            <button onClick={() => setGenModal(false)} className="btn-ghost">
              Cancelar
            </button>
            <button
              onClick={generateSessions}
              disabled={generating || !genForm.entity || !genForm.date_start}
              className="btn-gold flex items-center gap-2"
            >
              <Zap size={14} />
              {generating
                ? 'Procesando...'
                : isSpecialClass
                  ? 'Programar clase'
                  : 'Generar sesiones'}
            </button>
          </div>
        </div>
      </Modal>

      {/* Modal detalle sesión */}
      <Modal
        open={!!sessionDetail}
        onClose={() => setSessionDetail(null)}
        title="Detalle de Sesión"
        size="lg"
      >
        {sessionDetail && (
          <div className="space-y-4" data-testid="session-detail-modal">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <p className="label">
                  {sessionDetail.special_class_id || sessionDetail.is_special_class
                    ? 'Clase especial'
                    : sessionDetail.group?.class_type === 'individual'
                      ? 'Sesión individual'
                      : 'Grupo'}
                </p>
                <div className="flex items-center gap-2">
                  {(sessionDetail.special_class_id || sessionDetail.is_special_class) && (
                    <Star size={14} className="text-warning" />
                  )}
                  {sessionDetail.group?.class_type === 'individual' && (
                    <User size={14} className="text-silver" />
                  )}
                  <p className="text-white font-medium">{sessionLabel(sessionDetail)}</p>
                </div>
                {sessionSede(sessionDetail) && (
                  <p className="text-text-secondary text-sm flex items-center gap-1 mt-1">
                    <MapPin size={12} className="flex-shrink-0" />
                    {sessionSede(sessionDetail)}
                  </p>
                )}
              </div>
              <div>
                <p className="label">Fecha y hora</p>
                {editingSchedule ? (
                  <div className="space-y-2">
                    <input
                      type="date"
                      className="input text-sm py-1"
                      value={scheduleForm.date}
                      onChange={(e) => setScheduleForm((f) => ({ ...f, date: e.target.value }))}
                    />
                    <div className="flex gap-2">
                      <input
                        type="time"
                        className="input text-sm py-1"
                        value={scheduleForm.time_start}
                        onChange={(e) =>
                          setScheduleForm((f) => ({ ...f, time_start: e.target.value }))
                        }
                      />
                      <input
                        type="time"
                        className="input text-sm py-1"
                        value={scheduleForm.time_end}
                        onChange={(e) =>
                          setScheduleForm((f) => ({ ...f, time_end: e.target.value }))
                        }
                      />
                    </div>
                    <div className="flex gap-2">
                      <button
                        onClick={saveSchedule}
                        disabled={savingSchedule}
                        className="btn-gold text-xs px-2 py-1"
                      >
                        {savingSchedule ? (
                          '...'
                        ) : (
                          <>
                            <Check size={12} />
                          </>
                        )}
                      </button>
                      <button
                        onClick={() => setEditingSchedule(false)}
                        className="btn-ghost text-xs px-2 py-1"
                      >
                        <XIcon size={12} />
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="flex items-center gap-2">
                    <span className="text-white">
                      {format(parseISO(sessionDetail.date), 'dd/MM/yyyy')} ·{' '}
                      {sessionDetail.time_start.slice(0, 5)} — {sessionDetail.time_end.slice(0, 5)}
                    </span>
                    {sessionDetail.status !== 'completed' && (
                      <button
                        onClick={() => {
                          setScheduleForm({
                            date: sessionDetail.date,
                            time_start: sessionDetail.time_start,
                            time_end: sessionDetail.time_end,
                          });
                          setEditingSchedule(true);
                        }}
                        className="text-text-secondary hover:text-gold-primary transition-colors"
                      >
                        <Edit2 size={14} />
                      </button>
                    )}
                  </div>
                )}
              </div>
              <div>
                <p className="label">Plazas</p>
                {editingCapacity ? (
                  <div className="flex items-center gap-2">
                    <input
                      type="number"
                      min={Math.max(1, sessionDetail.current_count)}
                      className="input text-sm py-1 w-20"
                      value={capacityValue}
                      onChange={(e) => setCapacityValue(parseInt(e.target.value) || 0)}
                    />
                    <button
                      onClick={saveCapacity}
                      disabled={savingCapacity}
                      className="btn-gold text-xs px-2 py-1"
                    >
                      {savingCapacity ? '...' : <Check size={12} />}
                    </button>
                    <button
                      onClick={() => setEditingCapacity(false)}
                      className="btn-ghost text-xs px-2 py-1"
                    >
                      <XIcon size={12} />
                    </button>
                  </div>
                ) : (
                  <div className="flex items-center gap-2">
                    <p className="font-oswald text-gold-primary text-xl">
                      {sessionDetail.current_count}/{sessionDetail.max_students}
                    </p>
                    {sessionDetail.status !== 'completed' && (
                      <button
                        onClick={() => {
                          setCapacityValue(sessionDetail.max_students);
                          setEditingCapacity(true);
                        }}
                        className="text-text-secondary hover:text-gold-primary transition-colors"
                      >
                        <Edit2 size={14} />
                      </button>
                    )}
                  </div>
                )}
                {waitlistUsers.length > 0 && (
                  <p className="text-amber-400 text-xs mt-1 flex items-center gap-1">
                    <Clock size={11} />
                    {waitlistUsers.length} en lista de espera
                  </p>
                )}
              </div>
              <div>
                <p className="label">Estado</p>
                <span
                  className={
                    sessionDetail.status === 'scheduled'
                      ? 'badge-gold'
                      : sessionDetail.status === 'completed'
                        ? 'badge-success'
                        : 'badge-error'
                  }
                >
                  {sessionDetail.status === 'scheduled'
                    ? 'Programada'
                    : sessionDetail.status === 'completed'
                      ? 'Completada'
                      : 'Cancelada'}
                </span>
              </div>
              <div className="col-span-2">
                <p className="label">Entrenador</p>
                {editingMonitor ? (
                  <div className="flex items-center gap-2">
                    <select
                      className="input flex-1"
                      value={editMonitorId}
                      onChange={(e) => setEditMonitorId(e.target.value)}
                    >
                      <option value="">Sin asignar</option>
                      <OpcionesDeEntrenador lista={monitores} />
                    </select>
                    <button onClick={saveSessionMonitor} className="btn-gold text-sm px-3 py-1.5">
                      Guardar
                    </button>
                    <button
                      onClick={() => setEditingMonitor(false)}
                      className="btn-ghost text-sm px-3 py-1.5"
                    >
                      Cancelar
                    </button>
                  </div>
                ) : (
                  <div className="flex items-center gap-2">
                    <span
                      className={
                        getEffectiveMonitor(sessionDetail) ? 'text-white' : 'text-text-secondary'
                      }
                    >
                      {getEffectiveMonitor(sessionDetail)?.full_name ?? 'Sin asignar'}
                    </span>
                    <button
                      onClick={() => {
                        setEditMonitorId(sessionDetail.monitor_id ?? '');
                        setEditingMonitor(true);
                      }}
                      className="text-text-secondary hover:text-gold-primary transition-colors"
                    >
                      <Edit2 size={14} />
                    </button>
                  </div>
                )}
              </div>
            </div>

            {/* ⚠️ Mirando este panel se decide abrir plaza, duplicar la clase y
                traspasar a quien espera. Si la lectura falló, la lista de
                espera sale vacía y eso se lee como «no hay nadie esperando».
                El bloque entero desaparece con `waitlistUsers.length > 0`, así
                que el aviso tiene que ir FUERA de esa condición. */}
            {detalleFallidos.length > 0 && (
              <div
                className="rounded-lg border border-error/40 bg-error/5 p-3"
                data-testid="detalle-fallidos"
                role="status"
              >
                <p className="text-error text-sm">
                  No se han podido leer {listaEnCastellano([...detalleFallidos])} de esta sesión. No
                  decidas nada sobre las plazas con esto delante.
                </p>
              </div>
            )}

            {waitlistUsers.length > 0 && (
              <div>
                <h4 className="font-oswald text-sm uppercase tracking-wider text-amber-400 mb-3 flex items-center gap-2">
                  <Clock size={14} /> Lista de espera ({waitlistUsers.length})
                </h4>
                <div className="space-y-1 max-h-32 overflow-y-auto mb-4">
                  {waitlistUsers.map((wu) => (
                    <div
                      key={wu.user_id}
                      className="flex items-center justify-between py-1.5 px-3 rounded bg-amber-400/5 border border-amber-400/20"
                    >
                      <div className="flex items-center gap-2">
                        <span className="text-amber-400 text-xs font-oswald w-4">
                          {wu.waitlist_position}
                        </span>
                        <p className="text-white text-sm">{wu.full_name}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div>
              <h4 className="font-oswald text-sm uppercase tracking-wider text-white mb-3">
                Alumnos inscritos ({enrollments.filter((e) => e.status === 'confirmed').length})
              </h4>
              {enrollments.length === 0 ? (
                <p className="text-text-secondary text-sm">Sin inscripciones</p>
              ) : (
                <div className="space-y-1 max-h-48 overflow-y-auto">
                  {enrollments.map((e) => (
                    <div
                      key={e.id}
                      className="flex items-center justify-between py-2 px-3 rounded bg-bg-elevated"
                    >
                      <div className="flex items-center gap-3">
                        <Users size={14} className="text-text-secondary" />
                        <p className="text-white text-sm">{e.profiles?.full_name}</p>
                        {e.profiles?.tiene_ficha && (
                          <button
                            onClick={() =>
                              void abrirFichaMedica(e.user_id, e.profiles?.full_name ?? '')
                            }
                            title="Información médica"
                            className="text-error hover:text-error/80 transition-colors"
                          >
                            <HeartPulse size={14} />
                          </button>
                        )}
                      </div>
                      <div
                        className="flex items-center gap-2"
                        data-testid={`enrollment-row-${e.id}`}
                      >
                        {e.status === 'confirmed' && (
                          <>
                            <button
                              onClick={() => handleSetAttendance(e.id, true)}
                              title="Marcar asistió"
                              data-testid={`attendance-yes-${e.id}`}
                              className={`px-2 py-1 rounded text-xs transition-colors ${
                                e.attendance === true
                                  ? 'badge-success'
                                  : 'bg-bg-surface text-text-secondary hover:text-success border border-bg-border'
                              }`}
                            >
                              Asistió
                            </button>
                            <button
                              onClick={() => handleSetAttendance(e.id, false)}
                              title="Marcar no asistió"
                              data-testid={`attendance-no-${e.id}`}
                              className={`px-2 py-1 rounded text-xs transition-colors ${
                                e.attendance === false
                                  ? 'badge-error'
                                  : 'bg-bg-surface text-text-secondary hover:text-error border border-bg-border'
                              }`}
                            >
                              No asistió
                            </button>
                          </>
                        )}
                        <span
                          className={e.status === 'confirmed' ? 'badge-success' : 'badge-warning'}
                        >
                          {e.status === 'confirmed' ? 'Confirmado' : 'En espera'}
                        </span>
                        {sessionDetail?.status === 'scheduled' && (
                          <button
                            onClick={() =>
                              handleRemoveStudent(e.user_id, e.profiles?.full_name ?? 'alumno')
                            }
                            disabled={removingStudentId === e.user_id}
                            title="Dar de baja"
                            className="text-error hover:text-error/80 transition-colors disabled:opacity-50"
                          >
                            <UserMinus size={15} />
                          </button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {sessionDetail.status === 'scheduled' && (
              <div className="flex items-center justify-between pt-2 border-t border-bg-border gap-3 flex-wrap">
                <div className="flex items-center gap-2">
                  {waitlistUsers.length > 0 && (
                    <button
                      onClick={() => {
                        setDupForm({
                          date: sessionDetail.date,
                          time_start: sessionDetail.time_start,
                          time_end: sessionDetail.time_end,
                          max_students: sessionDetail.max_students,
                          monitor_id: sessionDetail.monitor_id ?? '',
                        });
                        setDuplicateError('');
                        setDuplicateModal(true);
                      }}
                      className="btn-outline flex items-center gap-2"
                    >
                      <Copy size={15} /> Duplicar clase ({waitlistUsers.length} en espera)
                    </button>
                  )}
                  {canMoveSession(sessionDetail) && (
                    <button
                      onClick={openMove}
                      className="btn-outline flex items-center gap-2"
                      data-testid="session-move-btn"
                    >
                      <CalendarClock size={15} /> Mover sesión
                    </button>
                  )}
                  <button
                    onClick={() => {
                      setAddStudentError('');
                      setAddStudentId('');
                      setAddStudentModal(true);
                    }}
                    className="btn-outline flex items-center gap-2"
                  >
                    <UserPlus size={15} /> Añadir alumno
                  </button>
                </div>
                <button
                  onClick={() => {
                    setSessionDetail(null);
                    setConfirmCancel(sessionDetail);
                  }}
                  className="btn-danger flex items-center gap-2"
                >
                  <XCircle size={16} /> Cancelar sesión
                </button>
              </div>
            )}

            {sessionDetail.status === 'cancelled' && (
              <div className="flex items-center justify-end pt-2 border-t border-bg-border gap-2 flex-wrap">
                <button
                  onClick={() => void askReactivateSession()}
                  className="btn-outline flex items-center gap-2"
                >
                  <RotateCcw size={15} /> Reactivar sesión
                </button>
                <button
                  onClick={() => setConfirmDeleteSession(sessionDetail)}
                  className="btn-danger flex items-center gap-2"
                >
                  <Trash2 size={15} /> Borrar definitivamente
                </button>
              </div>
            )}
          </div>
        )}
      </Modal>

      <Confirm
        open={!!confirmCancel}
        onClose={() => setConfirmCancel(null)}
        onConfirm={() => confirmCancel && cancelSession(confirmCancel)}
        title="Cancelar sesión"
        message="Se cancelará la sesión, se devolverán los créditos a todos los inscritos y se les notificará. Esta acción no se puede deshacer."
        confirmLabel="Cancelar sesión"
        danger
      />

      <Confirm
        open={!!confirmReactivate}
        onClose={() => setConfirmReactivate(null)}
        onConfirm={() => confirmReactivate && void reactivateSession(confirmReactivate.session)}
        title="Reactivar sesión"
        message={mensajeReactivar(confirmReactivate)}
        confirmLabel="Reactivar"
      />

      <Confirm
        open={!!confirmDeleteSession}
        onClose={() => setConfirmDeleteSession(null)}
        onConfirm={() => confirmDeleteSession && deleteSession(confirmDeleteSession)}
        title="Borrar sesión definitivamente"
        message="Esta sesión y todo su historial (inscripciones, asistencia, ratings) quedará eliminado de la BD. No se puede deshacer."
        confirmLabel="Borrar"
        danger
      />

      {/* Aviso de solapamiento de entrenador (C-2). No bloquea: al confirmar se
          ejecuta la acción original (INSERT/UPDATE) que quedó pendiente. */}
      <ConflictWarningModal
        open={!!conflictModal}
        conflicts={conflictModal?.conflicts ?? []}
        comprobado={conflictModal?.comprobado ?? true}
        confirmLabel={conflictModal?.confirmLabel}
        onClose={() => setConflictModal(null)}
        onConfirm={() => {
          void conflictModal?.onProceed();
        }}
      />

      {/* Modal mover sesión (M-2). Cambia fecha/hora/entrenador/sede conservando
          a los inscritos (RPC admin_move_session). Antes del RPC comprueba el
          solapamiento del entrenador (no bloquea). slot_taken = error bloqueante. */}
      <Modal open={moveModal} onClose={() => setMoveModal(false)} title="Mover sesión">
        <div className="space-y-4" data-testid="move-session-modal">
          <p className="text-text-secondary text-sm">
            Los jugadores inscritos se mantienen y reciben aviso (in-app + push) del cambio.
          </p>

          {moveError && (
            <div
              className="bg-error/10 border border-error/30 rounded-lg p-3 text-error text-sm"
              data-testid="move-session-error"
            >
              {moveError}
            </div>
          )}

          <div className="grid grid-cols-2 gap-4">
            <div className="col-span-2">
              <label className="label">Nueva fecha *</label>
              <input
                type="date"
                className="input"
                value={moveForm.date}
                onChange={(e) => setMoveForm((f) => ({ ...f, date: e.target.value }))}
                data-testid="move-date"
              />
            </div>
            <div>
              <label className="label">Hora inicio *</label>
              <input
                type="time"
                className="input"
                value={moveForm.time_start}
                onChange={(e) => setMoveForm((f) => ({ ...f, time_start: e.target.value }))}
                data-testid="move-time-start"
              />
            </div>
            <div>
              <label className="label">Hora fin *</label>
              <input
                type="time"
                className="input"
                value={moveForm.time_end}
                onChange={(e) => setMoveForm((f) => ({ ...f, time_end: e.target.value }))}
                data-testid="move-time-end"
              />
            </div>
            <div className="col-span-2">
              <label className="label">Sede</label>
              <select
                className="input"
                value={moveForm.sede_id}
                onChange={(e) => setMoveForm((f) => ({ ...f, sede_id: e.target.value }))}
                data-testid="move-sede-select"
              >
                <option value="">Heredar la del grupo</option>
                {activeSedes.map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.name}
                  </option>
                ))}
              </select>
              {activeSedesError && (
                <p className="text-error text-xs mt-1" data-testid="move-sedes-error">
                  No se han podido cargar las sedes: esta lista está incompleta.
                </p>
              )}
            </div>
            <div className="col-span-2">
              <label className="label">Entrenador</label>
              <select
                className="input"
                value={moveForm.monitor_id}
                onChange={(e) => setMoveForm((f) => ({ ...f, monitor_id: e.target.value }))}
                data-testid="move-monitor-select"
              >
                <option value="">Sin asignar</option>
                <OpcionesDeEntrenador lista={monitores} />
              </select>
            </div>
          </div>

          <div className="flex justify-end gap-2 pt-2">
            <button onClick={() => setMoveModal(false)} className="btn-ghost">
              Cancelar
            </button>
            <button
              onClick={handleMove}
              disabled={moving}
              className="btn-gold flex items-center gap-2"
              data-testid="move-session-submit"
            >
              <CalendarClock size={15} /> {moving ? 'Moviendo…' : 'Mover sesión'}
            </button>
          </div>
        </div>
      </Modal>

      {/* Modal duplicar clase */}
      <Modal open={duplicateModal} onClose={() => setDuplicateModal(false)} title="Duplicar clase">
        <div className="space-y-4">
          <p className="text-text-secondary text-sm">
            Se creará una nueva sesión y los {waitlistUsers.length} alumno
            {waitlistUsers.length > 1 ? 's' : ''} en lista de espera quedarán inscritos
            automáticamente (consumiendo su bono). Reciben notificación push. Si algún alumno no
            tuviera bono, queda en lista de espera de la nueva sesión.
          </p>

          <div className="grid grid-cols-2 gap-4">
            <div className="col-span-2">
              <label className="label">Nueva fecha *</label>
              <input
                type="date"
                className="input"
                data-testid="dup-date"
                value={dupForm.date}
                onChange={(e) => setDupForm((f) => ({ ...f, date: e.target.value }))}
              />
            </div>
            <div>
              <label className="label">Hora inicio *</label>
              <input
                type="time"
                className="input"
                value={dupForm.time_start}
                onChange={(e) => setDupForm((f) => ({ ...f, time_start: e.target.value }))}
              />
            </div>
            <div>
              <label className="label">Hora fin *</label>
              <input
                type="time"
                className="input"
                value={dupForm.time_end}
                onChange={(e) => setDupForm((f) => ({ ...f, time_end: e.target.value }))}
              />
            </div>
            <div className="col-span-2">
              <label className="label">Aforo máximo *</label>
              <input
                type="number"
                className="input"
                min={1}
                value={dupForm.max_students}
                onChange={(e) =>
                  setDupForm((f) => ({ ...f, max_students: parseInt(e.target.value) || 0 }))
                }
              />
            </div>
            {/* §26: entrenador editable al duplicar. Pre-rellenado al
                monitor original, pero el admin puede asignar otro o
                dejarla sin asignar. */}
            <div className="col-span-2">
              <label className="label">Entrenador</label>
              <select
                className="input"
                value={dupForm.monitor_id}
                onChange={(e) => setDupForm((f) => ({ ...f, monitor_id: e.target.value }))}
                data-testid="dup-monitor-select"
              >
                <option value="">Sin asignar</option>
                <OpcionesDeEntrenador lista={monitores} />
              </select>
            </div>
          </div>

          {duplicateError && <p className="text-error text-sm">{duplicateError}</p>}

          <div className="flex justify-end gap-3">
            <button onClick={() => setDuplicateModal(false)} className="btn-ghost">
              Cancelar
            </button>
            <button
              onClick={handleDuplicate}
              disabled={
                duplicating ||
                !dupForm.date ||
                !dupForm.time_start ||
                !dupForm.time_end ||
                dupForm.max_students < 1
              }
              className="btn-gold flex items-center gap-2"
            >
              <Copy size={14} />
              {duplicating ? 'Creando...' : 'Crear clase e inscribir lista de espera'}
            </button>
          </div>
        </div>
      </Modal>

      {/* Modal añadir alumno */}
      <Modal
        open={addStudentModal}
        onClose={() => setAddStudentModal(false)}
        title="Añadir alumno a la sesión"
      >
        <div className="space-y-4">
          <p className="text-text-secondary text-sm">
            Por defecto se consume una sesión del bono del alumno (FIFO). Marca "cortesía" para
            inscribirlo sin coste.
          </p>
          <div>
            <label className="label">Seleccionar alumno *</label>
            <select
              className="input"
              value={addStudentId}
              onChange={(e) => {
                setAddStudentId(e.target.value);
                setAddStudentError('');
              }}
            >
              <option value="">Seleccionar alumno...</option>
              {alumnos.map((a) => (
                <option key={a.id} value={a.id}>
                  {a.full_name}
                </option>
              ))}
            </select>
          </div>

          <label className="flex items-center gap-2 text-sm text-white cursor-pointer">
            <input
              type="checkbox"
              checked={addStudentCourtesy}
              onChange={(e) => setAddStudentCourtesy(e.target.checked)}
              className="w-4 h-4 accent-gold-primary"
            />
            Inscripción de cortesía (no consumir bono)
          </label>

          {addStudentError && <p className="text-error text-sm">{addStudentError}</p>}

          <div className="flex justify-end gap-3">
            <button
              onClick={() => {
                setAddStudentModal(false);
                setAddStudentCourtesy(false);
              }}
              className="btn-ghost"
            >
              Cancelar
            </button>
            <button
              onClick={handleAddStudent}
              disabled={addingStudent || !addStudentId}
              className="btn-gold flex items-center gap-2"
            >
              <UserPlus size={14} />
              {addingStudent ? 'Añadiendo...' : 'Añadir alumno'}
            </button>
          </div>
        </div>
      </Modal>

      {/* Modal info médica */}
      <Modal
        open={!!medicalInfoModal}
        onClose={() => setMedicalInfoModal(null)}
        title="Información médica"
      >
        {medicalInfoModal && (
          <div className="space-y-3">
            <p className="text-white font-medium">{medicalInfoModal.name}</p>
            <div className="bg-error/10 border border-error/40 rounded p-3 text-sm text-white whitespace-pre-wrap">
              {medicalInfoModal.estado === 'cargando'
                ? 'Cargando…'
                : medicalInfoModal.estado === 'error'
                  ? `No se pudo leer la ficha. ${medicalInfoModal.mensaje ?? ''}`
                  : medicalInfoModal.info || 'Sin información médica registrada.'}
            </div>
            <div className="flex justify-end">
              <button onClick={() => setMedicalInfoModal(null)} className="btn-ghost">
                Cerrar
              </button>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
}
